/**
 * Electron Renderer - Ticket Management Panel
 * 
 * PC側のチケット作成・管理・QR生成画面
 */

import React, { useState, useEffect, useRef } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import jsQR from 'jsqr';
import ExternalScannerPanel from './ExternalScannerPanel';
import {
  getFirebaseTickets,
  initFirebaseTickets,
  observeAuthState,
  loginWithGoogle,
  handleRedirectResult,
  loginWithLine,
  loginAnonymously,
  linkAnonymousWithProvider,
  logoutUser,
  generateUserUniqueId,
  getUserProfile,
  saveUserProfile,
  getUserTickets,
  migrateTicketsCreatedBy,
  readLocalTickets,
  createTicket,
  updateTicket,
  deleteTicket,
  generateAndSaveQRCodes,
  generateQRCodeImages,
  getStoredQRCodes,
  generateQRCodeCSV,
  importQRCodeFreeTextFromCSV,
  generateQRCodePDF,
  downloadFile,
  createStripeCheckoutSession,
  verifyStripeCheckoutSession,
  getUserSubscriptions,
  syncUserDataFromFirestoreToLocal,
  checkTicketQRAvailability,
  addPlanAddition,
  processTicketEntry,
  clearLocalRuntimeData,
  processPurchase,
  subscribeToUserTickets,
  subscribeToTicket,
  subscribeToQREntries,
  manualSetEntryStatus,
  saveQRComment
} from './ticketSystemApi';
import { performVersionCheck } from './versionCheck';
import {
  ensureInitialUserId,
  setStoredInitialUserId,
  getStoredInitialUserId,
  setProviderSession,
  clearProviderSession,
  clearStoredIdentity,
  resolveCanonicalUserIdByProvider,
  normalizeEmail
} from './authIdentity';
import { availableTemplates, ticketSizes } from './ticketTemplates';
import { generateDesignedTicketPDF, generateTemplatePreview } from './ticketPdfGenerator';
import {
  generateDesignedTicketPDF_A4Layout,
  generateDesignedTicketPDF_Single,
  generateDesignedTicketPDF_Mobile
} from './ticketPdfGenerator_v2';
import { saveUserCanonicalLink } from './ticketSystemApi';
import './TicketPanel.css';
import { getTranslation, formatMessage } from './i18n';
import config from './config';

const STRIPE_PUBLISHABLE_KEY = config.stripePublishableKey;
const stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null;

const TicketPanel = () => {
  // Electron環境の検出（より確実な方法）
  const isElectronEnv = typeof window !== 'undefined' && window.electron !== undefined;
  const isWebRuntime = !isElectronEnv;
  
  console.log('[TicketPanel] Environment detection:');
  console.log('[TicketPanel] window.electron:', typeof window !== 'undefined' ? window.electron : 'undefined');
  console.log('[TicketPanel] isElectronEnv:', isElectronEnv);
  console.log('[TicketPanel] isWebRuntime:', isWebRuntime);

  // ========================================
  // State
  // ========================================
  
  // 認証状態
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [userUniqueId, setUserUniqueId] = useState(null);
  
  // サブスクリプション・課金管理
  const [subscriptionSlots, setSubscriptionSlots] = useState([]);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(false);
  const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
  const [selectedPlanForPurchase, setSelectedPlanForPurchase] = useState('slot10');
  const [purchaseQuantity, setPurchaseQuantity] = useState('');
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [products, setProducts] = useState([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  
  const [appVersion, setAppVersion] = useState(process.env.REACT_APP_VERSION || '1.0.0');

  // チケット一覧
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [selectedTicketFromListId, setSelectedTicketFromListId] = useState(null);
  const [isLoadingTickets, setIsLoadingTickets] = useState(false);
  const [ticketListPage, setTicketListPage] = useState(1);
  const [ticketListSort, setTicketListSort] = useState('newest'); // 'newest', 'oldest', 'name'
  const [ticketListSearch, setTicketListSearch] = useState('');
  const TICKETS_PER_PAGE = 5;
  
  // チケット作成フォーム
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    name: ''
  });
  
  // QRコード生成フォーム
  const [showQRForm, setShowQRForm] = useState(false);
  const [qrCount, setQrCount] = useState('');
  const [isGeneratingQR, setIsGeneratingQR] = useState(false);
  const [qrActionTab, setQrActionTab] = useState('create');
  
  // 生成済みQRコード
  const [generatedQRCodes, setGeneratedQRCodes] = useState([]);
  const [showQRPreview, setShowQRPreview] = useState(false);
  
  // PDF レイアウト選択ダイアログ
  const [showPDFLayoutDialog, setShowPDFLayoutDialog] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('modern');
  const [selectedTicketSize, setSelectedTicketSize] = useState('standard'); // 'standard' or 'mobile'
  const [selectedLayout, setSelectedLayout] = useState('a4'); // 'a4', 'single', or 'mobile'
  const [ticketEventDate, setTicketEventDate] = useState('');
  const [ticketSeatInfo, setTicketSeatInfo] = useState('');
  const [isHtmlMode, setIsHtmlMode] = useState(false); // HTMLモードかプレーンテキストか
  const [showPreview, setShowPreview] = useState(false);
  const [showEntryScanner, setShowEntryScanner] = useState(false);
  const [scannerError, setScannerError] = useState(null);
  const [scannerResult, setScannerResult] = useState(null);
  const [manualEntryCode, setManualEntryCode] = useState('');
  const [isProcessingEntry, setIsProcessingEntry] = useState(false);
  const [confirmBeforeEntry, setConfirmBeforeEntry] = useState(false);
  const scannerVideoRef = useRef(null);
  const scannerCanvasRef = useRef(null);
  const scannerStreamRef = useRef(null);
  const scannerTimerRef = useRef(null);
  const scannerLastDetectedRef = useRef({ code: '', timestamp: 0 });
  
  // 外部デバイス許可管理
  const [showPermissionForm, setShowPermissionForm] = useState(false);
  const [isExternalAccessEnabled, setIsExternalAccessEnabled] = useState(false);
  const [ticketIdQRCodeDataUrl, setTicketIdQRCodeDataUrl] = useState('');
  
  // エラー・成功メッセージ
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState('info');  // 'info', 'success', 'error'
  const [activeMenu, setActiveMenu] = useState('tickets');
  const [showExternalScanner, setShowExternalScanner] = useState(false);  // 外部スキャナーモード
  const [isDownloadBusy, setIsDownloadBusy] = useState(false);
  const [lastDownloadedFilePath, setLastDownloadedFilePath] = useState(null);
  const [lastDownloadedFileName, setLastDownloadedFileName] = useState(null);
  const [downloadMessage, setDownloadMessage] = useState(null);
  const [downloadMessageType, setDownloadMessageType] = useState('info');

  // カスタム確認ダイアログ（Snap/DEBでシステムダイアログが豆腐になる対策）
  const [confirmDialog, setConfirmDialog] = useState(null); // { message, resolve, isPrompt, defaultValue }
  const confirmResolveRef = useRef(null);

  const showConfirm = (msg) => {
    return new Promise((resolve) => {
      confirmResolveRef.current = resolve;
      setConfirmDialog({ message: msg, isPrompt: false });
    });
  };

  const showPrompt = (msg, defaultValue = '') => {
    return new Promise((resolve) => {
      confirmResolveRef.current = resolve;
      setConfirmDialog({ message: msg, isPrompt: true, defaultValue });
    });
  };

  const handleConfirmOk = (value) => {
    if (confirmResolveRef.current) {
      confirmResolveRef.current(value !== undefined ? value : true);
      confirmResolveRef.current = null;
    }
    setConfirmDialog(null);
  };

  const handleConfirmCancel = () => {
    if (confirmResolveRef.current) {
      confirmResolveRef.current(confirmDialog?.isPrompt ? null : false);
      confirmResolveRef.current = null;
    }
    setConfirmDialog(null);
  };
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('appLanguage') || 'en';
  });
  
  // 入場状況タブ用
  const [entryStatusTicketId, setEntryStatusTicketId] = useState(null);
  const [entryStatusFilter, setEntryStatusFilter] = useState('all'); // 'all', 'entered', 'unused'
  const [entryStatusSort, setEntryStatusSort] = useState('time-desc'); // 'time-desc', 'time-asc', 'qr-asc', 'qr-desc'
  const [entryStatusPage, setEntryStatusPage] = useState(1);
  const [entryStatusQRCodes, setEntryStatusQRCodes] = useState([]);
  const [entryStatusSearchQuery, setEntryStatusSearchQuery] = useState('');
  const [entryStatusFreeTextSearch, setEntryStatusFreeTextSearch] = useState('');
  const [csvImportResult, setCsvImportResult] = useState(null); // { type: 'success'|'info'|'error', text: string }
  const ITEMS_PER_PAGE = 100;
  
  // レート制限用（連打防止）
  const lastDownloadTimeRef = useRef({
    csv: 0,
    images: 0,
    pdf: 0,
    designPdf: 0
  });
  const DOWNLOAD_COOLDOWN_APP = 3000; // アプリ版: 3秒
  const DOWNLOAD_COOLDOWN_WEB = 30000; // WEB版: 30秒
  
  const checkDownloadLimit = (type) => {
    const now = Date.now();
    const cooldown = isWebRuntime ? DOWNLOAD_COOLDOWN_WEB : DOWNLOAD_COOLDOWN_APP;
    const timeSinceLastDownload = now - lastDownloadTimeRef.current[type];
    
    if (timeSinceLastDownload < cooldown) {
      const remainingSeconds = Math.ceil((cooldown - timeSinceLastDownload) / 1000);
      return {
        allowed: false,
        message: formatMessage(language, 'cooldownMessage', { seconds: remainingSeconds }),
        showAppDownload: false
      };
    }
    
    lastDownloadTimeRef.current[type] = now;
    return { allowed: true };
  };
  
  const showDownloadLimitMessage = (message, showAppDownload) => {
    alert(message);
  };
  
  // ========================================
  // ライフサイクル
  // ========================================
  
  useEffect(() => {
    const initialize = async () => {
      setIsInitializing(true);

      // アプリバージョンを取得 (Electronの場合)
      let resolvedVersion = process.env.REACT_APP_VERSION || '1.0.0';
      if (isElectronEnv && window.electron && typeof window.electron.getAppVersion === 'function') {
        try {
          const ver = await window.electron.getAppVersion();
          console.log('[TicketPanel] App version from main process:', ver);
          resolvedVersion = ver;
          setAppVersion(ver);
        } catch (error) {
          console.warn('[TicketPanel] Failed to get app version:', error);
        }
      }
      
      // 1. まずリダイレクト認証の結果を処理
      try {
        console.log('[TicketPanel] Checking redirect result...');
        const user = await handleRedirectResult();
        if (user) {
          console.log('[TicketPanel] User logged in via redirect:', user.email);
          // 認証状態をリセット
          setIsAuthenticating(false);
          isLoginInProgress.current = false;
          // 認証状態が確立するまで少し待つ
          await new Promise(resolve => setTimeout(resolve, 1000));
        } else {
          console.log('[TicketPanel] No redirect result');
        }
      } catch (error) {
        console.error('[TicketPanel] Redirect result error:', error);
        // エラー時も認証状態をリセット
        setIsAuthenticating(false);
        isLoginInProgress.current = false;
      }

      // 2. バージョンチェックを実行（本番環境のみ）
      const checkVersion = async () => {
        if (typeof window !== 'undefined' && window.location.hostname === 'localhost' && window.location.port === '3000') {
          console.log('[TicketPanel] Skipping version check in development environment');
          return;
        }
        try {
          await performVersionCheck({
            currentVersion: resolvedVersion,
            showAlert: true,
            forceRedirect: true
          });
        } catch (error) {
          console.log('[TicketPanel] Version check skipped due to error');
        }
      };
      
      await checkVersion();

      // 3. Firebase初期化と認証状態の監視
      console.log('[TicketPanel] Starting auth state observation...');
      initFirebaseTickets();
      const unsubscribe = observeAuthState(() => {
        loadAuthState();
      });

      // 4. 初回の認証状態読み込み
      await loadAuthState();
      
      // 初期化完了
      setIsInitializing(false);

      return unsubscribe;
    };

    let unsubscribe;
    initialize().then(unsub => {
      unsubscribe = unsub;
    });

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, []);
  
  useEffect(() => {
    if (currentUser) {
      const syncAfterLogin = async () => {
        try {
          await syncUserDataFromFirestoreToLocal(currentUser.uid);
        } catch (error) {
          console.warn('Login sync skipped:', error);
        }

        // ローカルストレージをクリア（リアルタイムリスナーが最新データを取得する）
        if (typeof localStorage !== 'undefined') {
          try {
            localStorage.removeItem('localTickets');
            console.log('[TicketPanel] Cleared local tickets cache');
          } catch (error) {
            console.warn('Failed to clear local tickets:', error);
          }
        }

        await loadUserSubscription();
      };

      syncAfterLogin();
      
      // リアルタイムリスナーを開始（ユーザーのチケット一覧を監視）
      console.log('[TicketPanel] Starting realtime listener for user tickets:', currentUser.uid);
      const unsubscribeTickets = subscribeToUserTickets(currentUser.uid, (updatedTickets) => {
        console.log('[TicketPanel] Tickets updated from RTDB:', updatedTickets.length, updatedTickets);
        setTickets(updatedTickets);
        
        // ローカルストレージに保存
        if (typeof localStorage !== 'undefined') {
          try {
            localStorage.setItem('localTickets', JSON.stringify(updatedTickets));
            console.log('[TicketPanel] Saved tickets to localStorage');
          } catch (error) {
            console.warn('Failed to save tickets to localStorage:', error);
          }
        }
        
        // 選択中のチケットを更新
        setSelectedTicket(prev => {
          const currentId = prev?.id;
          if (currentId) {
            const updated = updatedTickets.find(t => t.id === currentId);
            return updated || prev;
          }
          if (updatedTickets.length > 0) {
            setSelectedTicketFromListId(updatedTickets[0].id);
            return updatedTickets[0];
          }
          return prev;
        });
      });
      
      // クリーンアップ
      return () => {
        console.log('[TicketPanel] Cleaning up realtime listener for user tickets');
        unsubscribeTickets();
      };
    }
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser?.uid || typeof window === 'undefined') {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const checkoutStatus = params.get('checkout');
    if (!checkoutStatus) {
      return;
    }

    const clearCheckoutQuery = () => {
      const nextUrl = `${window.location.origin}${window.location.pathname}`;
      window.history.replaceState({}, document.title, nextUrl);
    };

    if (checkoutStatus === 'cancel') {
      clearCheckoutQuery();
      showMessage(formatMessage(language, 'payment.cancelled'), 'info');
      return;
    }

    if (checkoutStatus !== 'success') {
      clearCheckoutQuery();
      return;
    }

    const plan = params.get('plan') || 'slot10';
    const quantity = Math.max(1, Number(params.get('quantity') || 1));
    const sessionId = String(params.get('session_id') || '').trim();
    const sessionKey = sessionId || `${currentUser.uid}:${plan}:${quantity}`;
    const processedKey = `stripeCheckoutProcessed:${sessionKey}`;

    if (sessionStorage.getItem(processedKey)) {
      clearCheckoutQuery();
      return;
    }

    sessionStorage.setItem(processedKey, '1');
    clearCheckoutQuery();

    const finalizePurchase = async () => {
      try {
        setIsPurchasing(true);

        if (!sessionId) {
          showMessage(formatMessage(language, 'payment.sessionNotFound'), 'error');
          return;
        }

        const verification = await verifyStripeCheckoutSession({ sessionId });
        if (!verification?.success || !verification?.applied) {
          showMessage(formatMessage(language, 'payment.pending'), 'info');
          return;
        }

        await syncUserDataFromFirestoreToLocal(currentUser.uid);
        await loadUserSubscription();
        await loadUserTickets();

        const addedQRCodes = quantity * 10;
        showMessage(formatMessage(language, 'payment.success', { 
          quantity: quantity, 
          slots: addedQRCodes.toLocaleString() 
        }), 'success');
      } catch (error) {
        showMessage(formatMessage(language, 'payment.verificationFailed'), 'error');
      } finally {
        setIsPurchasing(false);
      }
    };

    finalizePurchase();
  }, [currentUser?.uid]);

  // 商品マスターとサブスクリプションを読み込む
  useEffect(() => {
    if (currentUser && activeMenu === 'subscription') {
      loadProducts();
      loadUserSubscription();
    }
  }, [currentUser, activeMenu, language]);

useEffect(() => {
    let isCancelled = false;

    const generateTicketIdQRCode = async () => {
      if (!selectedTicket?.id) {
        setTicketIdQRCodeDataUrl('');
        return;
      }

      try {
        const qrCodeModule = await import('qrcode');
        const qrCodeApi = qrCodeModule?.default || qrCodeModule;
        const dataUrl = await qrCodeApi.toDataURL(String(selectedTicket.id), {
          errorCorrectionLevel: 'M',
          type: 'image/png',
          width: 220,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#ffffff'
          }
        });

        if (!isCancelled) {
          setTicketIdQRCodeDataUrl(dataUrl);
        }
      } catch (error) {
        if (!isCancelled) {
          setTicketIdQRCodeDataUrl('');
        }
        console.warn('Generate ticket ID QR failed:', error);
      }
    };

    generateTicketIdQRCode();

    return () => {
      isCancelled = true;
    };
  }, [selectedTicket?.id]);

  // Electron: ダウンロード開始/完了通知を購読（暗転表示・フォルダで表示用）
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!window.electron || typeof window.electron.onDownloadStatus !== 'function') return;

    const unsubscribe = window.electron.onDownloadStatus((payload) => {
      const state = payload?.state;
      if (state === 'started') {
        setIsDownloadBusy(true);
        setLastDownloadedFilePath(payload?.savePath || null);
        setLastDownloadedFileName(payload?.filename || null);
        return;
      }

      if (state === 'done') {
        setIsDownloadBusy(false);
        setLastDownloadedFilePath(payload?.savePath || null);
        setLastDownloadedFileName(payload?.filename || null);

        const result = payload?.result;
        if (result && result !== 'completed') {
          const label = result === 'cancelled' ? 'cancelled' : 'failed';
          showDownloadMessage(`Download ${label}.`, 'error');
        }
      }
    });

    return () => {
      try {
        if (typeof unsubscribe === 'function') unsubscribe();
      } catch {
        // no-op
      }
    };
  }, []);

  // Electron: downloadFile (IPC経由) の保存完了コールバック
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.__onElectronFileSaved = (result) => {
      if (result?.savePath) {
        setLastDownloadedFilePath(result.savePath);
        setLastDownloadedFileName(result.filename || null);
      }
    };
    return () => {
      delete window.__onElectronFileSaved;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (scannerTimerRef.current) {
        clearInterval(scannerTimerRef.current);
        scannerTimerRef.current = null;
      }

      if (scannerStreamRef.current) {
        scannerStreamRef.current.getTracks().forEach((track) => track.stop());
        scannerStreamRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    let isCancelled = false;

    const restoreStoredQRCodes = async () => {
      if (!selectedTicket?.id) {
        setGeneratedQRCodes([]);
        return;
      }

      try {
        // ローカルキャッシュから読み取り（Firestore読み取りを削減）
        const storedQRCodes = await getStoredQRCodes(selectedTicket.id, { preferLocal: true });
        if (!isCancelled) {
          setGeneratedQRCodes(Array.isArray(storedQRCodes) ? storedQRCodes : []);
        }
      } catch {
        if (!isCancelled) {
          setGeneratedQRCodes([]);
        }
      }
    };

    restoreStoredQRCodes();
    
    // リアルタイムリスナーを開始（選択中のチケット情報を監視）
    if (selectedTicket?.id) {
      console.log('[TicketPanel] Starting realtime listener for ticket:', selectedTicket.id);
      
      // チケット情報の監視
      const unsubscribeTicket = subscribeToTicket(selectedTicket.id, (updatedTicket) => {
        if (!isCancelled && updatedTicket) {
          console.log('[TicketPanel] Ticket info updated:', updatedTicket);
          setSelectedTicket(updatedTicket);
          
          // チケット一覧も更新
          setTickets(prevTickets => 
            prevTickets.map(t => t.id === updatedTicket.id ? updatedTicket : t)
          );
        }
      });
      
      // QR入場状態の監視
      const unsubscribeEntries = subscribeToQREntries(selectedTicket.id, (event) => {
        if (isCancelled) return;
        
        console.log('[TicketPanel] QR entry event:', event.type, event.code);
        
        // ローカルQRコードを更新
        if (event.type === 'added' || event.type === 'changed') {
          setGeneratedQRCodes(prevQRCodes => {
            const index = prevQRCodes.findIndex(qr => qr.code === event.code);
            if (index >= 0) {
              const updated = [...prevQRCodes];
              // imageDataを保持しながら更新
              const existingImageData = updated[index].imageData;
              updated[index] = { ...updated[index], ...event };
              // eventにimageDataがない場合は既存のものを保持
              if (!event.imageData && existingImageData) {
                updated[index].imageData = existingImageData;
              }
              return updated;
            }
            return prevQRCodes;
          });
          
          // ローカルストレージも更新
          if (typeof localStorage !== 'undefined') {
            try {
              const localKey = `localQRCodes:${selectedTicket.id}`;
              const localQRCodes = JSON.parse(localStorage.getItem(localKey) || '[]');
              const index = localQRCodes.findIndex(qr => qr.code === event.code);
              if (index >= 0) {
                // imageDataを保持しながら更新
                const existingImageData = localQRCodes[index].imageData;
                localQRCodes[index] = { ...localQRCodes[index], ...event };
                // eventにimageDataがない場合は既存のものを保持
                if (!event.imageData && existingImageData) {
                  localQRCodes[index].imageData = existingImageData;
                }
                localStorage.setItem(localKey, JSON.stringify(localQRCodes));
              }
            } catch (error) {
              console.warn('Failed to update localStorage:', error);
            }
          }
        }
      });
      
      // クリーンアップ
      return () => {
        isCancelled = true;
        console.log('[TicketPanel] Cleaning up realtime listeners for ticket:', selectedTicket.id);
        unsubscribeTicket();
        unsubscribeEntries();
      };
    }

    return () => {
      isCancelled = true;
    };
  }, [selectedTicket?.id]);
  
  // 入場状況タブ用のリアルタイムリスナー
  useEffect(() => {
    if (!entryStatusTicketId) {
      return;
    }
    
    console.log('[EntryStatus] Starting realtime listener for ticket:', entryStatusTicketId);
    
    // QR入場状態の監視
    const unsubscribeEntries = subscribeToQREntries(entryStatusTicketId, (event) => {
      console.log('[EntryStatus] QR entry event:', event.type, event.code);
      
      // ローカルQRコードを更新
      if (event.type === 'added' || event.type === 'changed') {
        setEntryStatusQRCodes(prevQRCodes => {
          const index = prevQRCodes.findIndex(qr => qr.code === event.code);
          if (index >= 0) {
            const updated = [...prevQRCodes];
            // imageDataを保持しながら更新
            const existingImageData = updated[index].imageData;
            updated[index] = { ...updated[index], ...event };
            // eventにimageDataがない場合は既存のものを保持
            if (!event.imageData && existingImageData) {
              updated[index].imageData = existingImageData;
            }
            console.log('[EntryStatus] Updated QR code:', updated[index].code, updated[index].entryStatus);
            return updated;
          }
          // 新規追加（リストに存在しない場合）
          if (event.type === 'added') {
            console.log('[EntryStatus] Added new QR code:', event.code);
            return [...prevQRCodes, event];
          }
          return prevQRCodes;
        });
      }
    });
    
    // クリーンアップ
    return () => {
      console.log('[EntryStatus] Cleaning up realtime listener for ticket:', entryStatusTicketId);
      unsubscribeEntries();
    };
  }, [entryStatusTicketId]);
  
  // ========================================
  // 認証関連
  // ========================================
  
  // ログイン処理中フラグ（loadAuthStateをスキップするため）
  const isLoginInProgress = useRef(false);
  // ログアウト処理中フラグ
  const isLogoutInProgress = useRef(false);
  // アカウント連携中フラグ（signInWithPopupでonAuthStateChangedが発火するのを無視するため）
  const isLinkingInProgress = useRef(false);

  const loadAuthState = async () => {
    // ログイン処理中はスキップ
    if (isLoginInProgress.current) {
      console.log('[loadAuthState] Skipped (login in progress)');
      return;
    }
    
    // ログアウト処理中はスキップ
    if (isLogoutInProgress.current) {
      console.log('[loadAuthState] Skipped (logout in progress)');
      return;
    }

    // アカウント連携中はスキップ（signInWithPopupでonAuthStateChangedが発火するため）
    if (isLinkingInProgress.current) {
      console.log('[loadAuthState] Skipped (linking in progress)');
      return;
    }

    try {
      console.log('[loadAuthState] Starting auth state load');
      console.log('[loadAuthState] Timestamp:', new Date().toISOString());
      
      const storedInitialUserId = getStoredInitialUserId();
      console.log('[loadAuthState] storedInitialUserId:', storedInitialUserId);
      
      if (storedInitialUserId) {
        setUserUniqueId(storedInitialUserId);
      }

      const { auth } = getFirebaseTickets();
      console.log('[loadAuthState] Firebase auth object:', auth ? 'exists' : 'null');
      const firebaseUser = auth?.currentUser;
      
      console.log('[loadAuthState] Firebase auth.currentUser:', firebaseUser ? firebaseUser.email : 'null');

      if (firebaseUser?.email) {
        console.log('[loadAuthState] Processing user:', firebaseUser.email);
        
        const providerData = firebaseUser.providerData?.[0];
        const providerId = providerData?.providerId || 'google.com';
        const provider = providerId.includes('line') ? 'line' : 'google';
        const email = normalizeEmail(firebaseUser.email);
        
        // auth_identity_linksから既存のcanonicalUserIdを取得
        // fallbackとしてのみ新しいIDを生成
        const fallbackUserId = isWebRuntime
          ? generateUserUniqueId()
          : (storedInitialUserId || generateUserUniqueId());
        
        console.log('[loadAuthState] fallbackUserId:', fallbackUserId);
        
        const identity = await resolveCanonicalUserIdByProvider({
          provider,
          providerEmail: email,
          firebaseUid: firebaseUser.uid,
          fallbackInitialUserId: fallbackUserId,
          displayName: firebaseUser.displayName,
          saveUserProfile
        });

        const canonicalUserId = identity.canonicalUserId;
        console.log('[loadAuthState] canonicalUserId:', canonicalUserId);
        console.log('[loadAuthState] isExistingLink:', identity.isExistingLink);

        // canonicalUserIdをlocalStorageに保存
        setStoredInitialUserId(canonicalUserId);
        setProviderSession({
          provider,
          email,
          displayName: firebaseUser.displayName || `${provider} User`
        });
        setUserUniqueId(canonicalUserId);

        // canonical ID → Firebase UID のマッピングを RTDB に保存
        // （ゲストから別アカウントに移行した際のチケット所有権確認に使用）
        await saveUserCanonicalLink(canonicalUserId, firebaseUser.uid);

        // 古いIDからcanonicalUserIdへ移行（fallbackUserIdが使われた場合のみ）
        if (!identity.isExistingLink) {
          await migrateTicketsCreatedBy(
            [fallbackUserId, email, firebaseUser.uid],
            canonicalUserId,
            firebaseUser.uid
          );
        }

        const profile = await getUserProfile(canonicalUserId);
        setCurrentUser({
          uid: canonicalUserId,
          displayName: profile?.displayName || firebaseUser.displayName || `${provider} User`,
          email: email,
          authProvider: provider,  // auth_identity_linksから取得した情報
          firebaseUid: firebaseUser.uid,
          role: profile?.role || 'user',
          ...(profile || {})
        });
        
        console.log('[loadAuthState] User state set successfully');
        return;
      }

      // ゲストセッションの復元（F5リロード対応）
      // localStorageにanonymousセッションが残っていれば引き続きゲストとして使用
      const storedProvider = localStorage.getItem('authProvider');
      if (storedInitialUserId && storedProvider === 'anonymous') {
        console.log('[loadAuthState] Restoring guest session:', storedInitialUserId);
        setUserUniqueId(storedInitialUserId);
        setCurrentUser({
          uid: storedInitialUserId,
          displayName: getTranslation(language, 'guestUser'),
          email: null,
          authProvider: 'anonymous',
          firebaseUid: firebaseUser?.uid || null,
          role: 'user'
        });
        return;
      }

      console.log('[loadAuthState] No Firebase user, setting currentUser to null');
      setCurrentUser(null);
      return;
    } catch (error) {
      console.error('[loadAuthState] Error:', error);
    }
  };
  
  const handleLineLogin = async () => {
    try {
      setIsAuthenticating(true);
      isLoginInProgress.current = true;  // ログイン処理開始
      console.log('[LINE Login] Starting LINE login...');
      
      const user = await loginWithLine();
      console.log('[LINE Login] User logged in:', user);

      const lineEmail = normalizeEmail(user.email || user.uid);
      console.log('[LINE Login] Email:', lineEmail);
      
      // auth_identity_linksから既存のcanonicalUserIdを取得
      // fallbackとしてのみ新しいIDを生成
      const fallbackUserId = isWebRuntime
        ? generateUserUniqueId()
        : ensureInitialUserId(generateUserUniqueId);
      
      const identity = await resolveCanonicalUserIdByProvider({
        provider: 'line',
        providerEmail: lineEmail,
        firebaseUid: user.uid,
        fallbackInitialUserId: fallbackUserId,
        displayName: user.displayName,
        saveUserProfile
      });

      const canonicalUserId = identity.canonicalUserId;
      setStoredInitialUserId(canonicalUserId);
      setProviderSession({
        provider: 'line',
        email: lineEmail,
        displayName: user.displayName || 'LINE User'
      });

      // 古いIDからcanonicalUserIdへ移行（fallbackUserIdが使われた場合のみ）
      if (!identity.isExistingLink) {
        await migrateTicketsCreatedBy(
          [fallbackUserId, lineEmail, user.uid],
          canonicalUserId,
          user.uid
        );
      }

      const profile = await getUserProfile(canonicalUserId);

      // resolveCanonicalUserIdByProvider内で既にsaveUserProfileが呼ばれているので
      // ここでは追加のフィールドがある場合のみ更新
      // await saveUserProfile(updatedProfile);  // ← 削除（重複呼び出し）

      setUserUniqueId(canonicalUserId);

      if (profile) {
        setCurrentUser({
          uid: canonicalUserId,
          displayName: profile.displayName || user.displayName || 'LINE User',
          email: lineEmail,
          authProvider: 'line',
          firebaseUid: user.uid,
          role: profile.role || 'user',
          ...profile
        });
      } else {
        setCurrentUser({
          uid: canonicalUserId,
          displayName: user.displayName || 'LINE User',
          email: lineEmail,
          authProvider: 'line',
          firebaseUid: user.uid,
          role: 'user'
        });
      }

      // ログアウトフラグをリセット（ログイン成功時）
      isLogoutInProgress.current = false;
      
      await loadUserTickets();

      showMessage(`${user.displayName} でログインしました`, 'success');
    } catch (error) {
      console.error('[LINE Login] Failed:', error);
      console.error('[LINE Login] Error code:', error?.code);
      console.error('[LINE Login] Error message:', error?.message);
      
      const errorCode = String(error?.code || '').trim();
      
      if (errorCode === 'auth/unauthorized-domain') {
        showMessage(formatMessage(language, 'login.lineUnauthorizedDomain'), 'error');
      } else if (errorCode === 'auth/operation-not-allowed') {
        showMessage(formatMessage(language, 'login.lineNotEnabled'), 'error');
      } else if (errorCode === 'auth/popup-blocked' || errorCode === 'auth/popup-closed-by-user') {
        showMessage(formatMessage(language, 'login.linePopupBlocked'), 'info');
      } else if (errorCode === 'auth/network-request-failed') {
        showMessage(formatMessage(language, 'login.lineNetworkError'), 'error');
      } else if (errorCode === 'auth/invalid-oauth-provider' || errorCode === 'auth/provider-not-found') {
        showMessage(formatMessage(language, 'login.lineProviderNotFound'), 'error');
      } else if (error.message.includes('popup') || error.message.includes('Redirect')) {
        showMessage(formatMessage(language, 'login.linePopupError'), 'info');
      } else {
        const suffix = errorCode ? ` (${errorCode})` : '';
        showMessage(`LINE ログインに失敗しました${suffix}`, 'error');
      }
    } finally {
      setIsAuthenticating(false);
      isLoginInProgress.current = false;  // ログイン処理終了
    }
  };
  
  const handleGoogleLogin = async () => {
    try {
      setIsAuthenticating(true);
      isLoginInProgress.current = true;
      console.log('[handleGoogleLogin] Starting Google login...');
      
      // Electronではリダイレクト、Webではポップアップを使用
      const user = await loginWithGoogle(!isWebRuntime);
      
      if (user) {
        // Webの場合はここで処理完了
        console.log('[handleGoogleLogin] Login successful:', user.email);
        showMessage(formatMessage(language, 'login.success', { email: user.email }), 'success');
        
        // 認証状態を更新
        setIsAuthenticating(false);
        isLoginInProgress.current = false;
        
        // 認証状態を再読み込み
        await loadAuthState();
      }
      // Electronの場合はリダイレクトが開始されるため、ここには到達しない
    } catch (error) {
      console.error('[handleGoogleLogin] Login error:', error);
      setIsAuthenticating(false);
      isLoginInProgress.current = false;
      
      const errorCode = String(error?.code || '').trim();
      if (errorCode === 'auth/unauthorized-domain') {
        showMessage(formatMessage(language, 'login.googleUnauthorizedDomain'), 'error');
      } else if (errorCode === 'auth/operation-not-allowed') {
        showMessage(formatMessage(language, 'login.googleNotEnabled'), 'error');
      } else {
        showMessage(`Google ログインに失敗しました (${errorCode})`, 'error');
      }
    }
  };
  
  const handleAnonymousLogin = async () => {
    try {
      setIsAuthenticating(true);
      isLoginInProgress.current = true;
      console.log('[handleAnonymousLogin] Starting anonymous login...');

      // 毎回新しいユニークIDを生成（ゲストセッションは常に新規）
      const newUserId = generateUserUniqueId();
      console.log('[handleAnonymousLogin] New guest user ID:', newUserId);

      const result = await loginAnonymously(newUserId);
      console.log('[handleAnonymousLogin] Firebase anonymous UID:', result.firebaseUid);

      setStoredInitialUserId(newUserId);
      setProviderSession({
        provider: 'anonymous',
        email: newUserId,
        displayName: getTranslation(language, 'guestUser')
      });

      const profile = await getUserProfile(result.uid);

      setUserUniqueId(result.uid);

      if (profile) {
        setCurrentUser({
          ...profile,
          uid: result.uid,
          displayName: profile.displayName || 'Anonymous User',
          email: result.firebaseUid,
          authProvider: 'anonymous',
          firebaseUid: result.firebaseUid,
          role: profile.role || 'user',
        });
      } else {
        setCurrentUser({
          uid: result.uid,
          displayName: 'Anonymous User',
          email: result.firebaseUid,
          authProvider: 'anonymous',
          firebaseUid: result.firebaseUid,
          role: 'user'
        });
      }

      await loadUserTickets();

      showMessage(getTranslation(language, 'guestLoginSuccess'), 'success');
    } catch (error) {
      console.error('Anonymous login failed:', error);
      showMessage(getTranslation(language, 'guestLoginFailed'), 'error');
    } finally {
      setIsAuthenticating(false);
      isLoginInProgress.current = false;
    }
  };

  const handleLinkWithGoogle = async () => {
    try {
      setIsAuthenticating(true);
      isLinkingInProgress.current = true;
      const result = await linkAnonymousWithProvider('google', currentUser.uid, () =>
        showConfirm(getTranslation(language, 'linkAlreadyLinkedWarning'))
      );
      if (result.cancelled) return;
      setStoredInitialUserId(result.uid);
      setUserUniqueId(result.uid);
      setCurrentUser(prev => ({
        ...prev,
        uid: result.uid,
        authProvider: 'google',
        displayName: result.displayName || prev.displayName,
        email: result.providerEmail,
        firebaseUid: result.firebaseUid,
        googleEmail: result.providerEmail
      }));
      setProviderSession({
        provider: 'google',
        email: result.providerEmail,
        displayName: result.displayName
      });
      await loadUserTickets();
      showMessage(result.wasAlreadyLinked ? getTranslation(language, 'linkAlreadyLinked') : formatMessage(language, 'linkSuccess', { provider: 'Google' }), 'success');
    } catch (error) {
      if (error.code !== 'auth/popup-closed-by-user' && error.code !== 'auth/popup-blocked') {
        showMessage(formatMessage(language, 'linkFailed', { provider: 'Google', error: error.code || error.message }), 'error');
      }
    } finally {
      isLinkingInProgress.current = false;
      setIsAuthenticating(false);
    }
  };

  const handleLinkWithLine = async () => {
    try {
      setIsAuthenticating(true);
      isLinkingInProgress.current = true;
      const result = await linkAnonymousWithProvider('line', currentUser.uid, () =>
        showConfirm(getTranslation(language, 'linkAlreadyLinkedWarning'))
      );
      if (result.cancelled) return;
      setStoredInitialUserId(result.uid);
      setUserUniqueId(result.uid);
      setCurrentUser(prev => ({
        ...prev,
        uid: result.uid,
        authProvider: 'line',
        displayName: result.displayName || prev.displayName,
        email: result.providerEmail,
        firebaseUid: result.firebaseUid
      }));
      setProviderSession({
        provider: 'line',
        email: result.providerEmail,
        displayName: result.displayName
      });
      await loadUserTickets();
      showMessage(result.wasAlreadyLinked ? getTranslation(language, 'linkAlreadyLinked') : formatMessage(language, 'linkSuccess', { provider: 'LINE' }), 'success');
    } catch (error) {
      if (error.code !== 'auth/popup-closed-by-user' && error.code !== 'auth/popup-blocked') {
        showMessage(formatMessage(language, 'linkFailed', { provider: 'LINE', error: error.code || error.message }), 'error');
      }
    } finally {
      isLinkingInProgress.current = false;
      setIsAuthenticating(false);
    }
  };

  const handleLogout = async () => {
    try {
      console.log('[handleLogout] Starting logout process');

      // ゲストモードの場合はデータ消失の警告を表示
      if (currentUser?.authProvider === 'anonymous') {
        const confirmed = await showConfirm(getTranslation(language, 'guestLogoutWarning'));
        if (!confirmed) return;
      }

      isLogoutInProgress.current = true;  // ログアウト処理開始

      if (currentUser?.uid) {
        try {
          await syncUserDataFromFirestoreToLocal(currentUser.uid);
        } catch (syncError) {
          console.warn('[handleLogout] Logout sync skipped:', syncError);
        }
      }

      // ログアウト前にFirebase authの状態を確認
      const { auth } = getFirebaseTickets();
      console.log('[handleLogout] Before logout - auth.currentUser:', auth?.currentUser?.email);

      // Firebase authからログアウト
      await logoutUser();
      console.log('[handleLogout] logoutUser() completed');

      // ログアウト後のFirebase authの状態を確認
      console.log('[handleLogout] After logout - auth.currentUser:', auth?.currentUser?.email);

      // セッション情報をクリア
      clearProviderSession();
      clearStoredIdentity();

      // ローカルストレージをクリア
      if (typeof localStorage !== 'undefined') {
        try {
          localStorage.removeItem('localTickets');
          localStorage.removeItem('localSubscriptions');
          console.log('[handleLogout] Cleared local storage');
        } catch (error) {
          console.warn('[handleLogout] Failed to clear local storage:', error);
        }
      }

      // 状態をリセット
      setUserUniqueId(null);
      setCurrentUser(null);
      setTickets([]);
      setSelectedTicket(null);
      setSubscriptionSlots([]);
      setGeneratedQRCodes([]);
      setShowQRPreview(false);

      console.log('[handleLogout] State reset completed');

      showMessage(formatMessage(language, 'login.logoutSuccess'), 'success');

      // ログアウトフラグを長めに保持（observeAuthStateのコールバックをブロック）
      setTimeout(() => {
        isLogoutInProgress.current = false;
        console.log('[handleLogout] Logout flag reset after 5 seconds');
      }, 5000);

      // 1秒後にFirebase authの状態を再確認
      setTimeout(() => {
        const { auth } = getFirebaseTickets();
        if (auth?.currentUser) {
          console.error('[handleLogout] ERROR: Firebase auth still has user after logout!');
          console.error('[handleLogout] User email:', auth.currentUser.email);
          console.error('[handleLogout] User UID:', auth.currentUser.uid);
          
          // 強制的にページをリロード（localStorageはクリア済み）
          console.warn('[handleLogout] Forcing page reload to complete logout');
          window.location.reload();
        } else {
          console.log('[handleLogout] Logout verified - no user in Firebase auth');
        }
      }, 1000);
    } catch (error) {
      console.error('[handleLogout] Logout failed with error:', error);
      isLogoutInProgress.current = false;  // エラー時もフラグ解除
      showMessage(formatMessage(language, 'login.logoutFailed'), 'error');
    }
  };

  // ========================================
  // サブスクリプション・課金管理
  // ========================================
  
  const loadProducts = async () => {
    try {
      setIsLoadingProducts(true);
      const { getFirestore } = await import('firebase/firestore');
      const { getOneTimeProducts } = await import('./productMaster');
      
      const firebaseTickets = getFirebaseTickets();
      const db = getFirestore(firebaseTickets.app, config.firestoreDatabaseId);
      
      console.log('[TicketPanel] Loading products from database:', config.firestoreDatabaseId);
      const productList = await getOneTimeProducts(db, language);
      console.log('[TicketPanel] Loaded products:', productList);
      
      if (productList.length === 0) {
        console.warn('[TicketPanel] No products found in Firestore. Please create products in admin panel.');
      }
      
      setProducts(productList);
    } catch (error) {
      console.error('Failed to load products:', error);
      console.error('Error details:', error.message, error.code);
      setProducts([]);
    } finally {
      setIsLoadingProducts(false);
    }
  };
  
  const loadUserSubscription = async () => {
    try {
      setIsLoadingSubscription(true);
      const allSubscriptions = await getUserSubscriptions(currentUser.uid);
      const now = new Date();

      const activeSlots = (allSubscriptions || [])
        .map((item) => {
          const expiresAtRaw = item.expiresAt?.toDate?.() || new Date(item.expiresAt);
          const expiresAt = expiresAtRaw instanceof Date ? expiresAtRaw : new Date(expiresAtRaw);
          return {
            ...item,
            expiresAt,
            daysRemaining: Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24))
          };
        })
        .filter((item) => item.status === 'active' && item.expiresAt > now)
        .sort((a, b) => b.expiresAt - a.expiresAt);

      setSubscriptionSlots(activeSlots);
    } catch (error) {
      console.warn('Load subscription skipped:', error);
      setSubscriptionSlots([]);
    } finally {
      setIsLoadingSubscription(false);
    }
  };
  
  const handlePurchasePlan = async (plan) => {
    setSelectedPlanForPurchase(plan);
    setPurchaseQuantity('');
    setShowPurchaseDialog(true);
  };
  
  const handleConfirmPurchase = async () => {
    try {
      setIsPurchasing(true);

      const safeQuantity = Math.max(1, Number(purchaseQuantity || 1));
      const baseReturnUrl = `${window.location.origin}${window.location.pathname}`;

      try {
        const session = await createStripeCheckoutSession({
          plan: selectedPlanForPurchase,
          userId: currentUser.uid,
          quantity: safeQuantity,
          successUrl: baseReturnUrl,
          cancelUrl: baseReturnUrl,
          language: language // 現在の言語を渡す
        });

        if (session?.url) {
          window.location.assign(session.url);
          return;
        }

        const stripe = await stripePromise;
        if (!stripe) {
          throw new Error('Stripe initialization failed');
        }

        const redirectResult = await stripe.redirectToCheckout({
          sessionId: session.sessionId
        });

        if (redirectResult?.error) {
          throw new Error(redirectResult.error.message || 'Checkout redirect failed');
        }
      } catch (stripeError) {
        console.error('Stripe checkout failed:', stripeError);
        throw new Error(stripeError.message || 'Stripe checkout failed');
      }
    } catch (error) {
      console.error('Purchase failed:', error);
      showMessage(formatMessage(language, 'purchaseError', { error: error.message }), 'error');
    } finally {
      setIsPurchasing(false);
    }
  };

  
  // ========================================
  // チケット管理
  // ========================================
  
  const loadUserTickets = async (targetUserId = currentUser?.uid) => {
    if (!targetUserId) {
      setTickets([]);
      setSelectedTicket(null);
      setSelectedTicketFromListId(null);
      return [];
    }

    try {
      setIsLoadingTickets(true);
      console.log('loadUserTickets: Starting for user:', targetUserId);
      
      // リアルタイムリスナーが動作している場合は、それに任せる
      // この関数は初回読み込みや手動更新時のみ使用
      const userTickets = await getUserTickets(targetUserId);
      console.log('loadUserTickets: Received from RTDB:', userTickets.length, 'tickets');
      setTickets(userTickets);

      if (userTickets.length === 0) {
        console.log('loadUserTickets: No tickets found');
        setSelectedTicket(null);
        setSelectedTicketFromListId(null);
      } else if (!selectedTicket || !userTickets.some(ticket => ticket.id === selectedTicket.id)) {
        setSelectedTicket(null);
        setSelectedTicketFromListId(null);
      }

      return userTickets;
    } catch (error) {
      console.warn('Load tickets skipped:', error);
      showMessage(formatMessage(language, 'ticket.loadFailed'), 'error');
      return [];
    } finally {
      setIsLoadingTickets(false);
    }
  };
  
  const handleCreateTicket = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      showMessage(formatMessage(language, 'ticket.nameRequired'), 'error');
      return;
    }
    
    try {
      setIsAuthenticating(true);
      
      const ticketData = {
        name: formData.name,
        plan: 'starter',
        createdBy: currentUser.uid,
        externalAccessEnabled: false,  // デフォルトはOFF
        maxQRCodes: 20,
        expiresAt: (() => {
          const date = new Date();
          date.setFullYear(date.getFullYear() + 1);
          return date;
        })()  // 発行日から1年
      };
      
      const ticketId = await createTicket(ticketData);
      
      // フォームクリア
      setFormData({ name: '' });
      setShowCreateForm(false);
      
      // チケット一覧再読み込み
      const refreshedTickets = await loadUserTickets();
      const createdTicket = refreshedTickets.find(ticket => ticket.id === ticketId);
      if (createdTicket) {
        // 状態を同時に更新して確実に反映させる
        setSelectedTicketFromListId(createdTicket.id);
        setSelectedTicket(createdTicket);
        setShowQRForm(true);
      }
      
      if (String(ticketId).startsWith('local_')) {
        showMessage(formatMessage(language, 'ticket.localOnly'), 'info');
      } else {
        showMessage(formatMessage(language, 'ticket.createSuccess', { name: formData.name }), 'success');
      }
    } catch (error) {
      console.warn('Create ticket skipped:', error);
      showMessage(formatMessage(language, 'ticket.createFailed', { error: error.message }), 'error');
    } finally {
      setIsAuthenticating(false);
    }
  };
  
  const handleDeleteTicket = async (ticketId) => {
    if (!await showConfirm(getTranslation(language, 'deleteTicketConfirm'))) return;
    
    try {
      const deleted = await deleteTicket(ticketId);
      if (!deleted) {
        showMessage(formatMessage(language, 'ticket.deleteLocalOnly'), 'info');
      }
      setTickets([]);
      setSelectedTicket(null);
      setSelectedTicketFromListId(null);
      if (currentUser?.uid) {
        await loadUserTickets(currentUser.uid);
      }
      setSelectedTicket(null);
      if (deleted) {
        showMessage(formatMessage(language, 'ticket.deleteSuccess'), 'success');
      }
    } catch (error) {
      console.error('Delete ticket failed:', error);
      showMessage(formatMessage(language, 'ticket.deleteFailed', { error: error.message }), 'error');
    }
  };
  
  // ========================================
  // QRコード生成
  // ========================================
  
  const handleGenerateQR = async (e) => {
    e.preventDefault();
    
    if (!selectedTicket) {
      showMessage(formatMessage(language, 'ticket.selectRequired'), 'error');
      return;
    }
    
    // 空文字列の場合はデフォルト値20を使用
    const requestedCount = qrCount === '' ? 20 : Number(qrCount);
    if (!Number.isFinite(requestedCount) || requestedCount <= 0) {
      showMessage(formatMessage(language, 'qr.countRequired'), 'error');
      return;
    }

    const remaining = Number(currentMaxQRCodes || 20) - Number(totalUsedQRCodes || 0);

    if (remaining <= 0) {
      const moveToBilling = await showConfirm('QR合計上限に達しました。課金ページへ移動しますか？');
      if (moveToBilling) {
        setActiveMenu('subscription');
      }
      return;
    }

    if (requestedCount > remaining) {
      const moveToBilling = await showConfirm(`残り発行可能枚数（合計）は ${remaining} 枚です。課金ページへ移動しますか？`);
      if (moveToBilling) {
        setActiveMenu('subscription');
      }
      return;
    }
    
    try {
      setIsGeneratingQR(true);

      const { auth } = getFirebaseTickets();
      const authUser = auth?.currentUser;
      const fallbackFirebaseUid = currentUser?.firebaseUid || authUser?.uid || null;
      const fallbackEmail = currentUser?.email || authUser?.email || null;

      const ownerBackfill = {};
      if (!selectedTicket.createdByFirebaseUid && fallbackFirebaseUid) {
        ownerBackfill.createdByFirebaseUid = fallbackFirebaseUid;
      }
      if (!selectedTicket.createdByEmail && fallbackEmail) {
        ownerBackfill.createdByEmail = fallbackEmail;
      }
      if (Object.keys(ownerBackfill).length > 0) {
        await updateTicket(selectedTicket.id, ownerBackfill);
      }
      
      // ステップ1: QRコード画像を生成
      const qrImages = await generateQRCodeImages(selectedTicket.id, requestedCount);
      
      // ステップ2: Firestore に保存（コード情報のみ）
      const qrCodesForDB = qrImages.map(qr => ({
        code: qr.code,
        imageData: qr.imageData
      }));
      const saveResult = await generateAndSaveQRCodes(selectedTicket.id, qrCodesForDB);
      
      // ステップ3: 生成済みQRコードを状態に保存
      setGeneratedQRCodes(qrImages);
      setShowQRPreview(true);
      setShowQRForm(false);
      
      // チケット情報再読み込み
      const refreshedTickets = await loadUserTickets();
      const refreshedSelectedTicket = refreshedTickets.find((ticket) => ticket.id === selectedTicket.id);
      if (refreshedSelectedTicket) {
        setSelectedTicket(refreshedSelectedTicket);
      }

      // 入場状況タブが同じチケットを表示中なら QRコード一覧を再取得
      if (entryStatusTicketId === selectedTicket.id) {
        try {
          const latestQRCodes = await getStoredQRCodes(selectedTicket.id, { preferLocal: true });
          if (Array.isArray(latestQRCodes)) {
            setEntryStatusQRCodes(latestQRCodes);
          }
        } catch (e) {
          console.warn('[handleGenerateQR] Failed to refresh entryStatusQRCodes:', e);
        }
      }

      if (saveResult?.storage === 'local') {
        const reason = saveResult?.errorCode ? `: ${saveResult.errorCode}` : '';
        showMessage(`${qrImages.length}個のQRコードを生成しました（DB保存に失敗${reason}、この端末に保存）`, 'info');
      } else {
        showMessage(`${qrImages.length}個のQRコードを生成しました`, 'success');
      }
      
      // 寄付ダイアログ表示（10回に1回）
      try {
        const qrGenerationCount = parseInt(localStorage.getItem('qrGenerationCount') || '0', 10);
        const newCount = qrGenerationCount + 1;
        localStorage.setItem('qrGenerationCount', newCount.toString());
        
        if (newCount % 10 === 0) {
          // 10回に1回、寄付ダイアログを表示
          setTimeout(async () => {
            const donationTitle = formatMessage(language, 'donation.title');
            const donationMessage = formatMessage(language, 'donation.message');
            const donationLater = formatMessage(language, 'donation.later');
            const donationDonate = formatMessage(language, 'donation.donate');
            
            const shouldDonate = await showConfirm(`${donationTitle}\n\n${donationMessage}`);
            if (shouldDonate) {
              window.open('https://buymeacoffee.com/matome2009', '_blank');
            }
          }, 1000); // 1秒後に表示
        }
      } catch (error) {
        console.warn('Donation dialog skipped:', error);
      }
    } catch (error) {
      console.warn('Generate QR skipped:', error);
      showMessage(formatMessage(language, 'qr.generateFailed', { error: error.message }), 'error');
    } finally {
      setIsGeneratingQR(false);
    }
  };

  const handleDownloadQRCSV = async () => {
    if (generatedQRCodes.length === 0 || !selectedTicket?.id) return;

    try {
      // 差分同期で最新データを取得（通信量削減）
      // preferLocal: trueでも、getStoredQRCodesは差分同期で最新の入場状態を取得してマージする
      const latestQRCodes = await getStoredQRCodes(selectedTicket.id, { preferLocal: true });
      const sourceQRCodes = Array.isArray(latestQRCodes) && latestQRCodes.length > 0
        ? latestQRCodes
        : generatedQRCodes;

      const csv = generateQRCodeCSV(sourceQRCodes, language);
      const fileName = `qr_codes_${selectedTicket.id}_${new Date().toISOString().split('T')[0]}.csv`;
      await downloadFile(csv, fileName, 'text/csv;charset=utf-8;');

      showDownloadMessage(formatMessage(language, 'qr.csvDownloadSuccess'), 'success');
    } catch (error) {
      console.warn('Download QR CSV skipped:', error);
      const csv = generateQRCodeCSV(generatedQRCodes, language);
      const fileName = `qr_codes_${selectedTicket.id}_${new Date().toISOString().split('T')[0]}.csv`;
      await downloadFile(csv, fileName, 'text/csv;charset=utf-8;');
      
      showDownloadMessage(formatMessage(language, 'qr.csvDownloadCache'), 'info');
    }
  };

  const handleImportQRCSV = async (event) => {
    if (!selectedTicket?.id) return;

    const file = event.target.files?.[0];
    if (!file) return;

    setCsvImportResult(null);
    try {
      const text = await file.text();
      const result = await importQRCodeFreeTextFromCSV(text, selectedTicket.id);
      
      if (result.errors.length > 0) {
        const msg = formatMessage(language, 'csvImportPartial', { updated: result.updatedCount, total: result.totalCount, errors: result.errors.length });
        setCsvImportResult({ type: 'info', text: msg });
        console.warn('Import errors:', result.errors);
      } else {
        const msg = formatMessage(language, 'csvImportSuccess', { updated: result.updatedCount });
        setCsvImportResult({ type: 'success', text: msg });
      }
      setTimeout(() => setCsvImportResult(null), 10000);

      // QRコードを再読み込み
      const refreshedQRCodes = await getStoredQRCodes(selectedTicket.id);
      setGeneratedQRCodes(refreshedQRCodes);
    } catch (error) {
      console.error('Import CSV failed:', error);
      const msg = formatMessage(language, 'csvImportFailed', { error: error.message });
      setCsvImportResult({ type: 'error', text: msg });
    }

    // ファイル入力をリセット
    event.target.value = '';
  };
  
  const handleDownloadQRImages = async () => {
    if (generatedQRCodes.length === 0) return;
    
    // レート制限チェック
    const limitCheck = checkDownloadLimit('images');
    if (!limitCheck.allowed) {
      showDownloadLimitMessage(limitCheck.message, limitCheck.showAppDownload);
      return;
    }
    
    try {
      const jszipModule = await import('jszip');
      const JSZipConstructor = jszipModule?.default || jszipModule;
      if (typeof JSZipConstructor !== 'function') {
        throw new Error('ZIPライブラリの初期化に失敗しました');
      }

      // imageDataがないQRコードがある場合は再生成
      const qrCodesWithImages = [];
      for (const qr of generatedQRCodes) {
        // codeが文字列でない場合は修正
        let codeString = qr.code;
        if (typeof codeString !== 'string') {
          if (codeString && typeof codeString === 'object') {
            codeString = codeString.value || codeString.code || codeString.text || String(codeString);
          } else {
            codeString = String(codeString);
          }
        }
        
        if (qr.imageData) {
          qrCodesWithImages.push({ ...qr, code: codeString });
        } else if (codeString) {
          // imageDataがない場合は再生成
          try {
            const QRCode = await import('qrcode');
            const imageData = await QRCode.toDataURL(codeString, {
              errorCorrectionLevel: 'M',
              type: 'image/png',
              width: 300,
              margin: 2
            });
            qrCodesWithImages.push({ ...qr, code: codeString, imageData });
          } catch (error) {
            console.warn('[Images] Failed to regenerate QR:', codeString, error);
          }
        }
      }

      const validItems = qrCodesWithImages
        .map((qr, index) => ({ qr, index }))
        .filter(({ qr }) => qr?.imageData && typeof qr.imageData === 'string' && qr.imageData.includes(','));

      if (validItems.length === 0) {
        showDownloadMessage(formatMessage(language, 'qr.noImages'), 'error');
        return;
      }

      const ticketId = selectedTicket?.id || 'ticket';
      const today = new Date().toISOString().split('T')[0];

      const zip = new JSZipConstructor();
      validItems.forEach(({ qr, index }) => {
        const commaIndex = qr.imageData.indexOf(',');
        const base64Data = qr.imageData.slice(commaIndex + 1);
        
        // codeが文字列でない場合は文字列に変換
        let codeString = qr.code;
        if (typeof codeString !== 'string') {
          if (codeString && typeof codeString === 'object') {
            codeString = codeString.value || codeString.code || codeString.text || String(codeString);
          } else {
            codeString = String(codeString);
          }
        }
        
        const safeSuffix = (codeString.slice(-8) || `item${index + 1}`).replace(/[^a-zA-Z0-9_-]/g, '_');
        const fileName = `qr_${index + 1}_${safeSuffix}.png`;
        zip.file(fileName, base64Data, { base64: true });
      });

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const zipFileName = `qr_images_${ticketId}_${today}.zip`;
      await downloadFile(zipBlob, zipFileName, 'application/zip');
      
      showDownloadMessage(`${validItems.length}個のQR画像をZIPでダウンロードしました`, 'success');
    } catch (error) {
      console.warn('Download images failed:', error);
      showDownloadMessage(`ダウンロードに失敗しました: ${error?.message || 'unknown error'}`, 'error');
    }
  };
  
  const handleDownloadQRPDF = async () => {
    if (generatedQRCodes.length === 0) return;

    // レート制限チェック
    const limitCheck = checkDownloadLimit('pdf');
    if (!limitCheck.allowed) {
      showDownloadLimitMessage(limitCheck.message, limitCheck.showAppDownload);
      return;
    }

    const layoutInput = await showPrompt(
      getTranslation(language, 'pdfLayoutPrompt'),
      '2'
    );

    if (layoutInput === null) {
      showDownloadMessage(formatMessage(language, 'qr.pdfCancelled'), 'info');
      return;
    }

    const layoutByInput = {
      '1': 'A4',
      '2': 'A4-4x5',
      '3': 'A4-4x6'
    };

    const layout = layoutByInput[String(layoutInput).trim()] || 'A4-4x5';
    
    try {
      setIsGeneratingQR(true);
      
      // imageDataがないQRコードがある場合は再生成
      const qrCodesWithImages = [];
      for (const qr of generatedQRCodes) {
        // codeが文字列でない場合は修正
        let codeString = qr.code;
        if (typeof codeString !== 'string') {
          if (codeString && typeof codeString === 'object') {
            codeString = codeString.value || codeString.code || codeString.text || String(codeString);
          } else {
            codeString = String(codeString);
          }
        }
        
        if (qr.imageData) {
          qrCodesWithImages.push({ ...qr, code: codeString });
        } else if (codeString) {
          // imageDataがない場合は再生成
          try {
            const QRCode = await import('qrcode');
            const imageData = await QRCode.toDataURL(codeString, {
              errorCorrectionLevel: 'M',
              type: 'image/png',
              width: 300,
              margin: 2
            });
            qrCodesWithImages.push({ ...qr, code: codeString, imageData });
          } catch (error) {
            console.warn('[PDF] Failed to regenerate QR:', codeString, error);
          }
        }
      }
      
      if (qrCodesWithImages.length === 0) {
        showDownloadMessage(getTranslation(language, 'noValidQRCodes'), 'error');
        return;
      }
      
      // PDF生成
      await generateQRCodePDF(qrCodesWithImages, selectedTicket.name, layout);
      
      showDownloadMessage(getTranslation(language, 'pdfLayoutGenerated').replace('{layout}', layout), 'success');
    } catch (error) {
      console.warn('Download PDF failed:', error);
      showDownloadMessage(formatMessage(language, 'qr.pdfFailed', { error: error.message }), 'error');
    } finally {
      setIsGeneratingQR(false);
    }
  };

  const handleGenerateDesignedPDF = async () => {
    if (!selectedTicket || !generatedQRCodes || generatedQRCodes.length === 0) {
      showDownloadMessage(formatMessage(language, 'qr.noGenerated'), 'error');
      return;
    }

    // レート制限チェック
    const limitCheck = checkDownloadLimit('designPdf');
    if (!limitCheck.allowed) {
      showDownloadLimitMessage(limitCheck.message, limitCheck.showAppDownload);
      return;
    }

    console.log('[Designed PDF] Starting generation...');
    console.log('[Designed PDF] Layout:', selectedLayout);

    try {
      setIsGeneratingQR(true);
      setShowTemplateDialog(false);
      
      // imageDataがないQRコードがある場合は再生成
      const qrCodesWithImages = [];
      for (const qr of generatedQRCodes) {
        // codeが文字列でない場合は修正
        let codeString = qr.code;
        if (typeof codeString !== 'string') {
          // codeがオブジェクトの場合、適切なプロパティを取得
          if (codeString && typeof codeString === 'object') {
            // codeオブジェクトの中身を確認
            console.warn('[Designed PDF] code is object:', codeString);
            // 可能性のあるプロパティから文字列を取得
            codeString = codeString.value || codeString.code || codeString.text || String(codeString);
          } else {
            codeString = String(codeString);
          }
        }
        
        if (qr.imageData) {
          qrCodesWithImages.push({ ...qr, code: codeString });
        } else if (codeString && typeof codeString === 'string') {
          console.log('[Designed PDF] Regenerating image for:', codeString);
          try {
            const QRCode = await import('qrcode');
            const imageData = await QRCode.toDataURL(codeString, {
              errorCorrectionLevel: 'M',
              type: 'image/png',
              width: 300,
              margin: 2
            });
            qrCodesWithImages.push({ ...qr, code: codeString, imageData });
          } catch (error) {
            console.warn('[Designed PDF] Failed to regenerate QR:', codeString, error);
          }
        } else {
          console.warn('[Designed PDF] Skipping invalid QR code:', qr);
        }
      }
      
      if (qrCodesWithImages.length === 0) {
        showDownloadMessage(getTranslation(language, 'noValidQRCodes'), 'error');
        return;
      }
      
      const pdfOptions = {
        eventDate: ticketEventDate || selectedTicket.eventDate || '',
        seatInfo: ticketSeatInfo || '',
        isHtmlMode: isHtmlMode,
        onProgress: (current, total) => {
          showDownloadMessage(`デザイン付きPDF生成中: ${current}/${total}`, 'info');
        }
      };

      // レイアウトに応じて関数を選択
      if (selectedLayout === 'a4') {
        await generateDesignedTicketPDF_A4Layout(
          qrCodesWithImages,
          selectedTicket.name,
          selectedTemplate,
          pdfOptions
        );
        
        showDownloadMessage(getTranslation(language, 'pdfGeneratedA4'), 'success');
      } else if (selectedLayout === 'mobile') {
        await generateDesignedTicketPDF_Mobile(
          qrCodesWithImages,
          selectedTicket.name,
          selectedTemplate,
          pdfOptions
        );
        
        showDownloadMessage(getTranslation(language, 'pdfGeneratedMobile'), 'success');
      } else {
        await generateDesignedTicketPDF_Single(
          qrCodesWithImages,
          selectedTicket.name,
          selectedTemplate,
          pdfOptions
        );
        
        showDownloadMessage(`デザイン付きPDF（1枚ずつ）を生成しました`, 'success');
      }
      
    } catch (error) {
      console.error('[Designed PDF] Generation failed:', error);
      showDownloadMessage(formatMessage(language, 'qr.designPdfFailed', { error: error.message }), 'error');
    } finally {
      setIsGeneratingQR(false);
    }
  };

  const closeEntryScanner = () => {
    if (scannerTimerRef.current) {
      clearInterval(scannerTimerRef.current);
      scannerTimerRef.current = null;
    }

    if (scannerStreamRef.current) {
      scannerStreamRef.current.getTracks().forEach((track) => track.stop());
      scannerStreamRef.current = null;
    }

    setShowEntryScanner(false);
    setScannerError(null);
    setScannerResult(null);
  };

  const handleEntryByCode = async (codeValue, source = 'pc-scanner') => {
    if (!selectedTicket?.id || !codeValue) return;

    try {
      setIsProcessingEntry(true);
      const result = await processTicketEntry(selectedTicket.id, codeValue, source);

      if (result.status === 'entry_accepted') {
        const resultId = result?.qrCodeId || codeValue;
        setScannerResult({ type: 'success', text: formatMessage(language, 'scanSuccess', { id: resultId }) });
        showMessage(formatMessage(language, 'scanSuccessShort', { id: resultId }), 'success');
        // チケット情報を再読み込みしてcheckedCountを更新
        const refreshedTickets = await loadUserTickets();
        const refreshedTicket = refreshedTickets.find((t) => t.id === selectedTicket.id);
        if (refreshedTicket) {
          setSelectedTicket(refreshedTicket);
        }
        return;
      }

      if (result.status === 'already_entered') {
        const enteredAtLabel = result?.enteredAt ? new Date(result.enteredAt).toLocaleString('ja-JP') : '不明';
        const resultId = result?.qrCodeId || codeValue;
        setScannerResult({ type: 'info', text: formatMessage(language, 'alreadyEnteredMessage', { id: resultId }) });
        window.alert(formatMessage(language, 'alreadyEnteredAlert', { id: resultId, time: enteredAtLabel }));
        showMessage(formatMessage(language, 'entry.alreadyEntered'), 'info');
        return;
      }

      if (result.status === 'not_found') {
        setScannerResult({ type: 'error', text: getTranslation(language, 'qrNotFound') });
        showMessage(formatMessage(language, 'entry.notFound'), 'error');
        return;
      }

      setScannerResult({ type: 'error', text: result.message || getTranslation(language, 'entryProcessFailed') });
      showMessage(result.message || getTranslation(language, 'entryProcessFailed'), 'error');
    } catch (error) {
      console.warn('Entry processing failed:', error);
      setScannerResult({ type: 'error', text: getTranslation(language, 'entryProcessFailed') });
      showMessage(formatMessage(language, 'entry.processFailed', { error: error.message }), 'error');
    } finally {
      setIsProcessingEntry(false);
    }
  };

  const handleManualEntrySubmit = async (e) => {
    e.preventDefault();
    const code = String(manualEntryCode || '').trim();
    if (!code) return;

    await handleEntryByCode(code, 'pc-manual-entry');
    setManualEntryCode('');
  };

  const openEntryScanner = async () => {
    if (!selectedTicket?.id) {
      showMessage(formatMessage(language, 'entry.selectTicketFirst'), 'error');
      return;
    }

    setShowEntryScanner(true);
    setScannerError(null);

    try {
      if (!navigator?.mediaDevices?.getUserMedia) {
        throw new Error(getTranslation(language, 'cameraNotAvailable'));
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false
      });

      scannerStreamRef.current = stream;

      if (scannerVideoRef.current) {
        scannerVideoRef.current.srcObject = stream;
        await scannerVideoRef.current.play();
      }

      // jsQR を使ったスキャン処理
      scannerTimerRef.current = window.setInterval(async () => {
        if (isProcessingEntry || !scannerVideoRef.current || !scannerCanvasRef.current) return;

        const video = scannerVideoRef.current;
        const canvas = scannerCanvasRef.current;
        const context = canvas.getContext('2d', { willReadFrequently: true });

        if (video.readyState === video.HAVE_ENOUGH_DATA) {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          context.drawImage(video, 0, 0, canvas.width, canvas.height);

          const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
          const code = jsQR(imageData.data, imageData.width, imageData.height, {
            inversionAttempts: 'dontInvert'
          });

          if (code && code.data) {
            const rawValue = String(code.data).trim();
            if (!rawValue) return;

            const now = Date.now();
            const lastDetected = scannerLastDetectedRef.current;
            if (lastDetected.code === rawValue && now - lastDetected.timestamp < 2500) {
              return;
            }
            scannerLastDetectedRef.current = { code: rawValue, timestamp: now };

            if (confirmBeforeEntry) {
              const ok = await showConfirm(formatMessage(language, 'scanConfirm', { id: rawValue }));
              if (!ok) {
                setScannerResult({ type: 'info', text: formatMessage(language, 'scanCancelled', { id: rawValue }) });
                return;
              }
            }

            handleEntryByCode(rawValue, 'pc-camera-entry');
          }
        }
      }, 500);
    } catch (error) {
      console.warn('Open entry scanner failed:', error);
      setScannerError(error.message || getTranslation(language, 'cameraStartFailed'));
    }
  };
  
  // ========================================
  // 外部デバイス許可管理
  // ========================================
  
  const handleToggleExternalAccess = async () => {
    if (!selectedTicket) return;
    
    try {
      const newStatus = !selectedTicket.externalAccessEnabled;
      await updateTicket(selectedTicket.id, {
        externalAccessEnabled: newStatus
      });
      
      // 選択チケットを更新
      setSelectedTicket({
        ...selectedTicket,
        externalAccessEnabled: newStatus
      });
      
      const message = newStatus 
        ? getTranslation(language, 'externalAccessEnabled')
        : getTranslation(language, 'externalAccessDisabled');
      showMessage(message, 'success');
    } catch (error) {
      console.error('Update external access failed:', error);
      showMessage(formatMessage(language, 'external.updateFailed', { error: error.message }), 'error');
    }
  };
  
  // ========================================
  // ユーティリティ
  // ========================================
  
  const showMessage = (text, type) => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => setMessage(null), 5000);
  };

  const showDownloadMessage = (text, type) => {
    setDownloadMessage(text);
    setDownloadMessageType(type);
    setTimeout(() => setDownloadMessage(null), 5000);
  };

  const handleLanguageChange = (newLang) => {
    setLanguage(newLang);
    localStorage.setItem('appLanguage', newLang);
    showMessage(getTranslation(newLang, 'languageChanged') || 'Language changed', 'success');
  };

  const handleCopyTicketId = async () => {
    const ticketId = String(selectedTicket?.id || '').trim();
    if (!ticketId) return;

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(ticketId);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = ticketId;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }

      showMessage(formatMessage(language, 'external.ticketIdCopied'), 'success');
    } catch (error) {
      console.warn('Copy ticket ID failed:', error);
      showMessage(formatMessage(language, 'external.copyFailed'), 'error');
    }
  };

  const totalUsedQRCodes = tickets.reduce((sum, ticket) => {
    return sum + Number(ticket.usedQRCodes || 0);
  }, 0);

  const totalPurchasedQRCodes = subscriptionSlots.reduce((sum, slot) => {
    return sum + Number(slot.maxQRCodes || 0);
  }, 0);

  const currentMaxQRCodes = Number(
    20 + totalPurchasedQRCodes
  );
  
  // ========================================
  // Render
  // ========================================
  
  // 初期化中は読み込み画面を表示
  if (isInitializing) {
    console.log('[TicketPanel Render] isInitializing:', isInitializing);
    return (
      <div className="AuthPanel">
        <h1>{getTranslation(language, 'ticketSystemTitle')}</h1>
        <p>初期化中...</p>
        <p style={{ fontSize: '12px', color: '#666' }}>
          {new Date().toLocaleTimeString()}
        </p>
      </div>
    );
  }
  
  console.log('[TicketPanel Render] currentUser:', currentUser ? currentUser.email : 'null');
  console.log('[TicketPanel Render] isAuthenticating:', isAuthenticating);
  
  if (!currentUser) {
    const langLabels = {
      en: 'English',
      ja: '日本語',
      zh: '中文',
      ko: '한국어'
    };
    
    return (
      <div className="AuthPanel">
        <h1>{getTranslation(language, 'ticketSystemTitle')}</h1>
        
        {/* 言語選択ボタン */}
        <div style={{ 
          display: 'flex', 
          gap: '8px', 
          justifyContent: 'center', 
          marginBottom: '20px',
          flexWrap: 'wrap'
        }}>
          {['en', 'ja', 'zh', 'ko'].map(lang => (
            <button
              key={lang}
              onClick={() => handleLanguageChange(lang)}
              style={{
                padding: '6px 12px',
                border: language === lang ? '2px solid #4CAF50' : '1px solid #d1d5db',
                borderRadius: '6px',
                background: language === lang ? '#e8f5e9' : 'white',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: language === lang ? '600' : '400',
                transition: 'all 0.2s'
              }}
            >
              {langLabels[lang]}
            </button>
          ))}
        </div>
        
        <div className="Auth-buttons">
          <button 
            onClick={handleGoogleLogin}
            disabled={isAuthenticating}
            className="btn-primary"
          >
            {isAuthenticating ? getTranslation(language, 'loggingIn') : getTranslation(language, 'loginWithGoogle')}
          </button>
          
          <button 
            onClick={handleLineLogin}
            disabled={isAuthenticating}
            className="btn-primary"
            style={{ backgroundColor: '#00B900' }}
          >
            {isAuthenticating ? getTranslation(language, 'loggingIn') : getTranslation(language, 'loginWithLine')}
          </button>
          
          <button
            onClick={handleAnonymousLogin}
            disabled={isAuthenticating}
            className="btn-secondary"
          >
            {getTranslation(language, 'loginAsGuest')}
          </button>
        </div>
        
        <div className="Auth-external-scanner">
          <p className="external-scanner-hint">No login required mode:</p>
          <button 
            onClick={() => setShowExternalScanner(true)}
            className="btn-external-scanner"
            title={getTranslation(language, 'externalScannerModeTitle')}
          >
            🔓 {getTranslation(language, 'externalScannerMode')}
          </button>
        </div>
        
        {message && <div className={`message ${messageType}`}>{message}<button onClick={() => setMessage(null)} style={{marginLeft:'8px',background:'none',border:'none',cursor:'pointer',fontWeight:'bold'}}>✕</button></div>}
        
        {/* 外部スキャナーモード */}
        {showExternalScanner && (
          <div className="modal-overlay">
            <ExternalScannerPanel onClose={() => setShowExternalScanner(false)} language={language} />
          </div>
        )}
      </div>
    );
  }
  
  return (
    <div className="TicketPanel">
      
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <h1 style={{ margin: 0 }}>{getTranslation(language, 'ticketSystemTitle')}</h1>
          {currentUser?.authProvider === 'anonymous' && (
            <span style={{
              padding: '3px 10px',
              background: '#f59e0b',
              color: 'white',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: '600'
            }}>
              {getTranslation(language, 'guestMode')}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <a
            href="https://ticketsystem-4bc88.web.app/"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 12px',
              background: '#5865F2',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'background 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.background = '#4752C4'}
            onMouseOut={(e) => e.currentTarget.style.background = '#5865F2'}
            title="公式サイト"
          >
            🏠 {getTranslation(language, 'site')}
          </a>
          <a
            href="https://ticketsystem-4bc88.web.app/help.html"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 12px',
              background: '#10b981',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'background 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.background = '#059669'}
            onMouseOut={(e) => e.currentTarget.style.background = '#10b981'}
            title="ヘルプ・使い方"
          >
            ❓ {getTranslation(language, 'help')}
          </a>
        </div>
      </div>

      <div className="Top-menu">
        <button
          className={`menu-item ${activeMenu === 'tickets' ? 'active' : ''}`}
          onClick={() => setActiveMenu('tickets')}
        >
          <span className="menu-icon">🎫</span>
          <span className="menu-text">{getTranslation(language, 'tickets')}</span>
        </button>
        <button
          className={`menu-item ${activeMenu === 'entryStatus' ? 'active' : ''}`}
          onClick={() => setActiveMenu('entryStatus')}
        >
          <span className="menu-icon">📊</span>
          <span className="menu-text">{getTranslation(language, 'entryStatus')}</span>
        </button>
        <button
          className={`menu-item ${activeMenu === 'devices' ? 'active' : ''}`}
          onClick={() => setActiveMenu('devices')}
        >
          <span className="menu-icon">📱</span>
          <span className="menu-text">{getTranslation(language, 'externalDevicePermission')}</span>
        </button>
        <button
          className={`menu-item ${activeMenu === 'subscription' ? 'active' : ''}`}
          onClick={() => setActiveMenu('subscription')}
        >
          <span className="menu-icon">💳</span>
          <span className="menu-text">{getTranslation(language, 'qrExpansion')}</span>
        </button>
        <button
          className={`menu-item ${activeMenu === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveMenu('settings')}
        >
          <span className="menu-icon">⚙️</span>
          <span className="menu-text">{getTranslation(language, 'settings')}</span>
        </button>
      </div>

      {isDownloadBusy && (
        <div
          className="modal-overlay"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
        >
          <div
            style={{
              background: 'rgba(0,0,0,0.85)',
              color: '#fff',
              padding: '16px 20px',
              borderRadius: '10px',
              minWidth: '240px',
              textAlign: 'center'
            }}
          >
            <div style={{ fontSize: '14px', fontWeight: 'bold' }}>Downloading...</div>
            {lastDownloadedFileName && (
              <div style={{ fontSize: '12px', opacity: 0.9, marginTop: '8px', wordBreak: 'break-all' }}>
                {lastDownloadedFileName}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* メッセージ表示 */}
      {message && <div className={`message ${messageType}`}>{message}<button onClick={() => setMessage(null)} style={{marginLeft:'8px',background:'none',border:'none',cursor:'pointer',fontWeight:'bold'}}>✕</button></div>}

      {/* QR枠購入パネル */}
      {activeMenu === 'subscription' && (
      <div className="Subscription-section">
        <h2>{getTranslation(language, 'purchaseQRSlots')}</h2>
        
        {isLoadingSubscription || isLoadingProducts ? (
          <p>{getTranslation(language, 'loading')}</p>
        ) : (
          <>
            {/* Product Cards - Always show first */}
            <div className="Plan-options">
              {products.map((product) => (
                <div key={product.id} className="Plan-card basic">
                  <h3>{product.name}</h3>
                  <p className="Price">{product.description}</p>
                  
                  <button 
                    onClick={() => handlePurchasePlan(product.id)}
                    disabled={isPurchasing}
                    className="btn-success"
                  >
                    {getTranslation(language, 'purchaseButton')}
                  </button>
                </div>
              ))}
              
              {products.length === 0 && !isLoadingProducts && (
                <p>商品情報を読み込めませんでした</p>
              )}
            </div>
            
            {/* Purchase History List - Show below products if there are subscriptions */}
            {subscriptionSlots.length > 0 && (
              <>
                <div style={{
                  marginTop: '30px',
                  marginBottom: '20px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    padding: '12px 16px',
                    background: '#f5f5f5',
                    borderBottom: '1px solid #e0e0e0',
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}>
                    {getTranslation(language, 'purchaseHistory')}
                  </div>
                  <div style={{
                    maxHeight: '200px',
                    overflowY: 'auto',
                    overflowX: 'hidden'
                  }}>
                    {subscriptionSlots.map((slot, index) => (
                      <div 
                        key={`slot-${index}`}
                        style={{
                          padding: '12px 16px',
                          borderBottom: index < subscriptionSlots.length - 1 ? '1px solid #f0f0f0' : 'none',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          fontSize: '14px'
                        }}
                      >
                        <div>
                          <strong style={{ color: '#4CAF50' }}>
                            {Number(slot.maxQRCodes || 0).toLocaleString()}{getTranslation(language, 'remainingSlots')}
                          </strong>
                          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                            {formatMessage(language, 'daysRemaining', { days: slot.daysRemaining })}
                          </div>
                        </div>
                        <div style={{ fontSize: '12px', color: '#999' }}>
                          {slot.plan || 'N/A'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
          </>
        )}
        
        {/* 特定商取引法に基づく表記へのリンク */}
        <div className="commercial-law-link-section">
          <a 
            href="#" 
            onClick={(e) => {
              e.preventDefault();
              if (window.electron && window.electron.openExternal) {
                window.electron.openExternal('https://ticketsystem-4bc88.web.app/commercial-law.html');
              } else {
                window.open('https://ticketsystem-4bc88.web.app/commercial-law.html', '_blank');
              }
            }}
            className="commercial-law-link"
          >
            📄 {getTranslation(language, 'commercialLaw.title')}
          </a>
        </div>
          </>
        )}
      </div>
      )}

      
      {/* 購入確認ダイアログ */}
      {showPurchaseDialog && (
        <div className="Modal-overlay" onClick={() => !isPurchasing && setShowPurchaseDialog(false)}>
          <div className="Modal-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>{getTranslation(language, 'purchaseConfirmTitle')}</h3>
            
            {(() => {
              const selectedProduct = products.find(p => p.id === selectedPlanForPurchase);
              if (!selectedProduct) {
                return <p>商品情報が見つかりません</p>;
              }
              
              const quantity = Number(purchaseQuantity || 1);
              const totalSlots = quantity * selectedProduct.slots;
              const totalAmount = quantity * selectedProduct.unitAmount;
              
              return (
                <>
                  <p className="Modal-body">
                    {selectedProduct.detailDescription}
                  </p>

                  <label>
                    {getTranslation(language, 'purchaseQuantity')}:
                    <input
                      type="number"
                      min="1"
                      value={purchaseQuantity}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '') {
                          setPurchaseQuantity('');
                          return;
                        }
                        const next = Number.parseInt(value, 10);
                        if (!Number.isNaN(next) && next >= 1) {
                          setPurchaseQuantity(next);
                        }
                      }}
                      onBlur={(e) => {
                        if (e.target.value === '') {
                          setPurchaseQuantity(1);
                        }
                      }}
                      placeholder="1"
                      disabled={isPurchasing}
                    />
                  </label>

                  <p className="Modal-body">
                    {getTranslation(language, 'totalAmount')}: {quantity.toLocaleString()}件 / {totalSlots.toLocaleString()}枠 / ¥{totalAmount.toLocaleString()}
                  </p>
                </>
              );
            })()}
            
            <div className="Modal-buttons">
              <button 
                onClick={() => setShowPurchaseDialog(false)}
                disabled={isPurchasing}
                className="btn-secondary"
              >
                {getTranslation(language, 'cancel')}
              </button>
              
              <button 
                onClick={handleConfirmPurchase}
                disabled={isPurchasing}
                className="btn-success"
              >
                {isPurchasing ? getTranslation(language, 'processing') : getTranslation(language, 'confirmPurchase')}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* チケット一覧 */}
      {activeMenu === 'tickets' && (
      <div className="Tickets-section">
        <h2><span className="Section-label">{getTranslation(language, 'ticketList')}</span></h2>
        <p className="Ticket-total">{getTranslation(language, 'qrTotal')}: {totalUsedQRCodes} / {currentMaxQRCodes}</p>

        {/* 検索・ソート */}
        {!isLoadingTickets && tickets.length > 0 && (
          <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              type="text"
              value={ticketListSearch}
              onChange={(e) => { setTicketListSearch(e.target.value); setTicketListPage(1); }}
              placeholder={getTranslation(language, 'ticketNameSearch')}
              className="search-input"
              style={{ flex: '1', minWidth: '120px' }}
            />
            <select
              value={ticketListSort}
              onChange={(e) => { setTicketListSort(e.target.value); setTicketListPage(1); }}
              className="filter-select"
            >
              <option value="newest">{getTranslation(language, 'sortNewest')}</option>
              <option value="oldest">{getTranslation(language, 'sortOldest')}</option>
              <option value="name">{getTranslation(language, 'sortName')}</option>
            </select>
          </div>
        )}
        
        {isLoadingTickets ? (
          <p>{getTranslation(language, 'loading')}</p>
        ) : tickets.length === 0 ? (
          <p>{getTranslation(language, 'noTickets')}</p>
        ) : null}

        {!isLoadingTickets && tickets.length > 0 && (() => {
          const searchTerm = ticketListSearch.trim().toLowerCase();
          const filtered = searchTerm
            ? tickets.filter(t => (t.name || '').toLowerCase().includes(searchTerm))
            : tickets;
          const sorted = [...filtered].sort((a, b) => {
            if (ticketListSort === 'name') return (a.name || '').localeCompare(b.name || '');
            const aTime = a.createdAt?.toDate?.()?.getTime() || new Date(a.createdAt || 0).getTime();
            const bTime = b.createdAt?.toDate?.()?.getTime() || new Date(b.createdAt || 0).getTime();
            return ticketListSort === 'oldest' ? aTime - bTime : bTime - aTime;
          });
          const totalPages = Math.ceil(sorted.length / TICKETS_PER_PAGE);
          const paginated = sorted.slice((ticketListPage - 1) * TICKETS_PER_PAGE, ticketListPage * TICKETS_PER_PAGE);
          return (
            <>
              <ul className="Ticket-list">
                {paginated.map((ticket, index) => (
                  <li key={`ticket-list-${index}`}>
                    <button
                      type="button"
                      className={`Ticket-row ${selectedTicket?.id === ticket.id ? 'active' : ''}`}
                      onClick={() => { setSelectedTicket(ticket); setSelectedTicketFromListId(ticket.id); }}
                    >
                      <span className="Ticket-row-check" aria-hidden="true">
                        <input type="checkbox" tabIndex={-1} checked={selectedTicket?.id === ticket.id} readOnly />
                      </span>
                      <span className="Ticket-row-name">{ticket.name}</span>
                      <span className="Ticket-row-meta">{getTranslation(language, 'qrCount')}: {Number(ticket.usedQRCodes || 0)}</span>
                      <span className="Ticket-row-meta">{getTranslation(language, 'expiryDate')}: {new Date(ticket.expiresAt.toDate?.() || ticket.expiresAt).toLocaleDateString('ja-JP')}</span>
                    </button>
                  </li>
                ))}
                {paginated.length === 0 && (
                  <li style={{ padding: '12px', color: '#999', fontSize: '14px' }}>{getTranslation(language, 'noSearchResults')}</li>
                )}
              </ul>
              {totalPages > 1 && (
                <div className="EntryStatus-pagination" style={{ marginTop: '8px' }}>
                  <button onClick={() => setTicketListPage(p => Math.max(1, p - 1))} disabled={ticketListPage === 1} className="pagination-btn">← {getTranslation(language, 'prevPage')}</button>
                  <span className="pagination-info">{ticketListPage} / {totalPages}</span>
                  <button onClick={() => setTicketListPage(p => Math.min(totalPages, p + 1))} disabled={ticketListPage === totalPages} className="pagination-btn">{getTranslation(language, 'nextPage')} →</button>
                </div>
              )}
            </>
          );
        })()}
        
        <button 
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="btn-primary"
          style={{ marginTop: '10px' }}
        >
          {showCreateForm ? getTranslation(language, 'cancel') : getTranslation(language, 'createNewTicket')}
        </button>
      </div>
      )}
      
      {/* チケット作成フォーム */}
      {activeMenu === 'tickets' && showCreateForm && (
        <form onSubmit={handleCreateTicket} className="Form-section">
          <h2>新規チケット作成</h2>
          
          <label>
            チケット名:
            <input 
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="例: 演奏会2026"
              disabled={isAuthenticating}
            />
          </label>

          
          <button 
            type="submit"
            disabled={isAuthenticating}
            className="btn-success"
          >
            {isAuthenticating ? '作成中...' : '作成'}
          </button>
        </form>
      )}
      
      {/* チケット詳細・操作パネル */}
      {activeMenu === 'tickets' && selectedTicket && selectedTicketFromListId === selectedTicket.id && (
        <div className="Detail-section">
          <h2 className="Detail-title">{getTranslation(language, 'ticketDetails')} <span>{selectedTicket.name}</span></h2>
          
          <div className="Detail-info">
              <p><strong>{getTranslation(language, 'issued')}:</strong> {selectedTicket.checkedCount || 0}/{Number(selectedTicket.usedQRCodes || 0)}</p>
            <p><strong>{getTranslation(language, 'expiryDate')}:</strong> {new Date(selectedTicket.expiresAt.toDate?.() || selectedTicket.expiresAt).toLocaleDateString('ja-JP')}</p>
          </div>
          
          {/* QRコード生成 */}
          <div className="QR-section">
            <h3>{getTranslation(language, 'qrOperations')}</h3>

            <div className="QR-action-tabs" role="tablist" aria-label="QR操作タブ">
              <button
                type="button"
                className={`qr-tab-item ${qrActionTab === 'create' ? 'active' : ''}`}
                onClick={() => setQrActionTab('create')}
              >
                <span className="qr-tab-icon">➕</span>
                <span className="qr-tab-text">{getTranslation(language, 'createQR')}</span>
              </button>
              <button
                type="button"
                className={`qr-tab-item ${qrActionTab === 'read' ? 'active' : ''}`}
                onClick={() => setQrActionTab('read')}
              >
                <span className="qr-tab-icon">📷</span>
                <span className="qr-tab-text">{getTranslation(language, 'readQR')}</span>
              </button>
            </div>

            {qrActionTab === 'create' && (
              <div className="qr-create-content">
                {!showQRForm ? (
                  <button 
                    onClick={() => setShowQRForm(true)}
                    className="btn-primary"
                  >
                    {Number(selectedTicket?.usedQRCodes || 0) > 0 ? getTranslation(language, 'additionalGeneration') : getTranslation(language, 'generate')}
                  </button>
                ) : (
                  <form onSubmit={handleGenerateQR}>
                    <label>
                      {getTranslation(language, 'generateCount')}:
                      <input 
                        type="number"
                        min="1"
                        value={qrCount}
                        onChange={(e) => {
                          const value = e.target.value;
                          // 空文字列の場合はそのまま設定（ユーザーが削除できるように）
                          if (value === '') {
                            setQrCount('');
                            return;
                          }
                          // 数値に変換して1以上に制限
                          const nextValue = Number.parseInt(value, 10);
                          if (!Number.isNaN(nextValue) && nextValue >= 1) {
                            setQrCount(nextValue);
                          }
                        }}
                        onBlur={(e) => {
                          // フォーカスが外れた時、空の場合は20を設定
                          if (e.target.value === '') {
                            setQrCount(20);
                          }
                        }}
                        placeholder="20"
                        disabled={isGeneratingQR}
                      />
                    </label>
                    
                    <button 
                      type="submit"
                      disabled={isGeneratingQR}
                      className="btn-success"
                    >
                      {isGeneratingQR ? getTranslation(language, 'generating') : getTranslation(language, 'generate')}
                    </button>
                    
                    <button 
                      type="button"
                      onClick={() => setShowQRForm(false)}
                      className="btn-secondary"
                    >
                      {getTranslation(language, 'cancel')}
                    </button>
                  </form>
                )}
              </div>
            )}

            {qrActionTab === 'read' && (
              <>
                <button
                  onClick={openEntryScanner}
                  className="btn-primary"
                  disabled={isProcessingEntry}
                >
                  {isProcessingEntry ? getTranslation(language, 'processing') : `📷 ${getTranslation(language, 'qrScanEntry')}`}
                </button>

                <label>
                  <input
                    type="checkbox"
                    checked={confirmBeforeEntry}
                    onChange={(e) => setConfirmBeforeEntry(e.target.checked)}
                  />
                  {getTranslation(language, 'confirmOnScan')}
                </label>

                <form onSubmit={handleManualEntrySubmit}>
                  <label>
                    {getTranslation(language, 'manualEntryCode')}:
                    <input
                      type="text"
                      value={manualEntryCode}
                      onChange={(e) => setManualEntryCode(e.target.value)}
                      placeholder={getTranslation(language, 'pasteQRString')}
                      disabled={isProcessingEntry}
                    />
                  </label>
                  <button type="submit" className="btn-secondary" disabled={isProcessingEntry || !manualEntryCode.trim()}>
                    {getTranslation(language, 'manualEntry')}
                  </button>
                </form>
              </>
            )}
          </div>
          
          {/* QRプレビュー・ダウンロード */}
          {generatedQRCodes.length > 0 && (
            <div className="QR-Preview-section">
              <h3>{getTranslation(language, 'generationComplete')}: {generatedQRCodes.length}{getTranslation(language, 'qrCodes')}</h3>
              
              <div className="Download-buttons">
                <button 
                  onClick={handleDownloadQRCSV}
                  className="btn-primary"
                  title={getTranslation(language, 'csvDownloadTitle')}
                >
                  📋 {getTranslation(language, 'downloadCSV')}
                </button>

                <label className="btn-primary" style={{ cursor: 'pointer' }}>
                  📥 {getTranslation(language, 'csvImport')}
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleImportQRCSV}
                    style={{ display: 'none' }}
                    title={getTranslation(language, 'csvImportTitle')}
                  />
                </label>
                {csvImportResult && (
                  <div className={`message ${csvImportResult.type}`} style={{ marginTop: '6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>{csvImportResult.type === 'success' ? '✅' : csvImportResult.type === 'error' ? '❌' : 'ℹ️'} {csvImportResult.text}</span>
                    <button onClick={() => setCsvImportResult(null)} style={{marginLeft:'8px',background:'none',border:'none',cursor:'pointer',fontWeight:'bold'}}>✕</button>
                  </div>
                )}
                
                <button 
                  onClick={handleDownloadQRImages}
                  className="btn-primary"
                  title={getTranslation(language, 'qrImageDownloadTitle')}
                >
                  🖼️ {getTranslation(language, 'downloadImages')}
                </button>
                
                <button 
                  onClick={handleDownloadQRPDF}
                  className="btn-primary"
                  disabled={isGeneratingQR}
                  title={getTranslation(language, 'pdfDownloadTitle')}
                >
                  {isGeneratingQR ? '⏳ ' + getTranslation(language, 'generating') : '📄 ' + getTranslation(language, 'downloadPDF')}
                </button>
                
                <button 
                  onClick={() => setShowTemplateDialog(true)}
                  className="btn-primary"
                  disabled={isGeneratingQR}
                  title={getTranslation(language, 'designPdfTitle')}
                >
                  {isGeneratingQR ? '⏳ ' + getTranslation(language, 'generating') : '🎨 ' + getTranslation(language, 'designPDF')}
                </button>
              </div>

              {/* ダウンロード通知（ボタンの直下に表示） */}
              {downloadMessage && (
                <div className={`message ${downloadMessageType}`} style={{ marginTop: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>{downloadMessage}</span>
                  <button onClick={() => setDownloadMessage(null)} style={{marginLeft:'8px',background:'none',border:'none',cursor:'pointer',fontWeight:'bold'}}>✕</button>
                </div>
              )}

              {lastDownloadedFilePath && window?.electron?.revealInFolder && (
                <div className="message info" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px' }}>
                  <div style={{ flex: 1, wordBreak: 'break-all' }}>
                    📁 Saved: {lastDownloadedFileName || lastDownloadedFilePath}
                  </div>
                  <button
                    className="btn-secondary"
                    onClick={async () => {
                      try {
                        await window.electron.revealInFolder(lastDownloadedFilePath);
                      } catch (e) {
                        showMessage(`Failed to open folder: ${e?.message || 'unknown error'}`, 'error');
                      }
                    }}
                  >
                    フォルダで表示
                  </button>
                  <button
                    onClick={() => {
                      setLastDownloadedFilePath(null);
                      setLastDownloadedFileName(null);
                    }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}
                    aria-label="dismiss"
                  >
                    ✕
                  </button>
                </div>
              )}
              
              <div className="Download-info">
                <p>
                  <strong>📋 {getTranslation(language, 'downloadCSV')}:</strong> {getTranslation(language, 'csvDownloadDesc')}<br/>
                  <strong>🖼️ {getTranslation(language, 'downloadImages')}:</strong> {getTranslation(language, 'imageDownloadDesc')}<br/>
                  <strong>📄 {getTranslation(language, 'downloadPDF')}:</strong> {getTranslation(language, 'pdfPrintDesc')}<br/>
                  <strong>🎨 {getTranslation(language, 'designPDF')}:</strong> {getTranslation(language, 'designPdfDesc')}
                </p>
              </div>
            </div>
          )}
          
          {/* チケット削除 */}
          <button 
            onClick={() => handleDeleteTicket(selectedTicket.id)}
            className="btn-danger"
          >
            {getTranslation(language, 'deleteTicket')}
          </button>
        </div>
      )}

      {showEntryScanner && (
        <div className="Modal-overlay" onClick={closeEntryScanner}>
          <div className="Modal-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>{getTranslation(language, 'qrScanner')}</h3>
            <video
              ref={scannerVideoRef}
              style={{ width: '100%', maxHeight: '360px', background: '#000' }}
              muted
              autoPlay
              playsInline
            />
            <canvas ref={scannerCanvasRef} style={{ display: 'none' }} />
            {scannerResult && <p className={`message ${scannerResult.type}`}>{scannerResult.text}</p>}
            {scannerError && <p className="message error">{scannerError}</p>}
            <div className="Modal-buttons">
              <button onClick={closeEntryScanner} className="btn-secondary">{getTranslation(language, 'close')}</button>
            </div>
          </div>
        </div>
      )}

      {activeMenu === 'devices' && (
        <div className="Devices-section">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
            <h2 style={{ margin: 0 }}>{getTranslation(language, 'externalDevicePermission')}</h2>
            <button
              className="btn-secondary"
              onClick={() => setShowExternalScanner(true)}
              title={getTranslation(language, 'externalScannerModeTitle')}
              style={{ whiteSpace: 'nowrap' }}
            >
              🔓 {getTranslation(language, 'externalScannerButton')}
            </button>
          </div>

          {tickets.length === 0 ? (
            <p>{getTranslation(language, 'noTicketsForPermission')}</p>
          ) : (
            <div className="Permission-section">
              <label>
                {getTranslation(language, 'selectTargetTicket')}
                <select
                  className="Settings-ticket-select"
                  value={selectedTicket?.id || ''}
                  onChange={(e) => {
                    const target = tickets.find((ticket) => ticket.id === e.target.value) || null;
                    setSelectedTicket(target);
                    setSelectedTicketFromListId(null);
                  }}
                >
                  <option value="">{getTranslation(language, 'selectTicketPlaceholder')}</option>
                  {tickets.map((ticket, index) => (
                    <option key={`ticket-select-${index}`} value={ticket.id}>
                      {ticket.name}
                    </option>
                  ))}
                </select>
              </label>

              {selectedTicket && (
                <>
                  <div className="Ticket-share-card">
                    <p><strong>{getTranslation(language, 'ticketId')}</strong></p>
                    <div className="Ticket-share-row">
                      <span className="key-id">{selectedTicket.id}</span>
                      <button
                        type="button"
                        onClick={handleCopyTicketId}
                        className="btn-secondary"
                      >
                        {getTranslation(language, 'copy')}
                      </button>
                    </div>
                    {ticketIdQRCodeDataUrl ? (
                      <img
                        src={ticketIdQRCodeDataUrl}
                        alt={getTranslation(language, 'ticketIdQRCode')}
                        className="Ticket-id-qr"
                      />
                    ) : (
                      <p className="Info-text">{getTranslation(language, 'generatingQRCode')}</p>
                    )}
                  </div>

                  <label>
                    <input 
                      type="checkbox"
                      checked={selectedTicket.externalAccessEnabled}
                      onChange={handleToggleExternalAccess}
                    />
                    {getTranslation(language, 'allowExternalScan')}
                  </label>

                  {selectedTicket.externalAccessEnabled && (
                    <p className="Info-text">
                      {getTranslation(language, 'allowExternalAccessDesc')}
                    </p>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {activeMenu === 'settings' && (
        <div className="Settings-section">
          <h2>{getTranslation(language, 'settings')}</h2>

          <div className="Language-section">
            <h3>{getTranslation(language, 'language')}</h3>
            <div className="Language-options">
              <button
                className={`language-btn ${language === 'ja' ? 'active' : ''}`}
                onClick={() => handleLanguageChange('ja')}
              >
                🇯🇵 日本語
              </button>
              <button
                className={`language-btn ${language === 'en' ? 'active' : ''}`}
                onClick={() => handleLanguageChange('en')}
              >
                🇺🇸 English
              </button>
              <button
                className={`language-btn ${language === 'ko' ? 'active' : ''}`}
                onClick={() => handleLanguageChange('ko')}
              >
                🇰🇷 한국어
              </button>
              <button
                className={`language-btn ${language === 'zh' ? 'active' : ''}`}
                onClick={() => handleLanguageChange('zh')}
              >
                🇨🇳 中文
              </button>
            </div>
          </div>

          {currentUser?.authProvider === 'anonymous' && (
            <div style={{ marginBottom: '24px', padding: '16px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '8px' }}>
              <h3 style={{ margin: '0 0 8px 0', color: '#92400e' }}>🔗 {getTranslation(language, 'linkAccount')}</h3>
              <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#78350f' }}>
                {getTranslation(language, 'linkAccountDesc')}
              </p>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button
                  onClick={handleLinkWithGoogle}
                  disabled={isAuthenticating}
                  className="btn-primary"
                >
                  {isAuthenticating ? getTranslation(language, 'loggingIn') : getTranslation(language, 'linkWithGoogle')}
                </button>
                <button
                  onClick={handleLinkWithLine}
                  disabled={isAuthenticating}
                  className="btn-primary"
                  style={{ backgroundColor: '#00B900' }}
                >
                  {isAuthenticating ? getTranslation(language, 'loggingIn') : getTranslation(language, 'linkWithLine')}
                </button>
              </div>
            </div>
          )}

          <div className="Account-actions">
            <button
              onClick={() => {
                window.open('https://buymeacoffee.com/matome2009', '_blank');
              }}
              className="btn-tip"
              title={getTranslation(language, 'tipDeveloper')}
            >
              ☕ {getTranslation(language, 'tipDeveloper')}
            </button>

            <button
              onClick={handleLogout}
              className="btn-logout"
              title={getTranslation(language, 'logoutTitle')}
            >
              🚪 {getTranslation(language, 'logout')}
            </button>

            <button
              onClick={async () => {
                if (await showConfirm(getTranslation(language, 'clearCacheConfirm'))) {
                  try {
                    // 不整合が出た場合は、ローカル側の全キャッシュを捨てて RTDB を正とする
                    const clearedCount = localStorage.length;
                    localStorage.clear();
                    showMessage(formatMessage(language, 'clearCacheSuccess', { count: clearedCount }), 'success');
                    console.log('[Settings] Cleared all localStorage items. count=', clearedCount);

                    // 即時に再取得するためリロード
                    window.location.reload();
                  } catch (error) {
                    console.error('[Settings] Failed to clear cache:', error);
                    showMessage(formatMessage(language, 'cache.clearFailed', { error: error.message }), 'error');
                  }
                }
              }}
              className="btn-danger"
              title={getTranslation(language, 'clearCacheTitle')}
            >
              🗑️ {getTranslation(language, 'clearLocalCache')}
            </button>


          </div>

          <div style={{ 
            marginTop: '40px', 
            textAlign: 'center', 
            fontSize: '12px', 
            color: '#999' 
          }}>
            {getTranslation(language, 'version')} {appVersion}
          </div>
        </div>
      )}

      {/* 入場状況 */}
      {activeMenu === 'entryStatus' && (
        <div className="EntryStatus-section">
          <h2>📊 {getTranslation(language, 'entryStatusTitle')}</h2>
          
          {/* チケット選択プルダウン */}
          <div className="EntryStatus-ticket-selector">
            <label>{getTranslation(language, 'ticketSelection')}:</label>
            <select
              value={entryStatusTicketId || ''}
              onChange={async (e) => {
                const ticketId = e.target.value;
                setEntryStatusTicketId(ticketId);
                setEntryStatusPage(1);
                
                if (ticketId) {
                  try {
                    console.log('[EntryStatus] Loading QR codes for ticket:', ticketId);
                    
                    // キャッシュを活用（差分同期で最新データを取得）
                    console.log('[EntryStatus] Fetching data with cache...');
                    const latestQRCodes = await getStoredQRCodes(ticketId, { preferLocal: true });
                    
                    if (latestQRCodes && latestQRCodes.length > 0) {
                      console.log('[EntryStatus] Loaded QR codes:', latestQRCodes.length);
                      console.log('[EntryStatus] Entered count:', latestQRCodes.filter(qr => qr.entryStatus === 'entered').length);
                      console.log('[EntryStatus] Sample QR code (first 3):', latestQRCodes.slice(0, 3).map(qr => ({
                        code: qr.code,
                        freeText: qr.freeText,
                        entryStatus: qr.entryStatus
                      })));
                      setEntryStatusQRCodes(Array.isArray(latestQRCodes) ? latestQRCodes : []);
                      showMessage(formatMessage(language, 'qrCodesLoaded', { count: latestQRCodes.length }), 'success');
                    } else {
                      setEntryStatusQRCodes([]);
                      showMessage(formatMessage(language, 'cache.noQRCodes'), 'info');
                    }
                  } catch (error) {
                    console.error('[EntryStatus] Failed to load QR codes:', error);
                    setEntryStatusQRCodes([]);
                    showMessage(formatMessage(language, 'cache.loadFailed', { error: error.message }), 'error');
                  }
                } else {
                  setEntryStatusQRCodes([]);
                }
              }}
              className="ticket-select"
            >
              <option value="">{getTranslation(language, 'selectTicketPlease')}</option>
              {tickets.map((ticket, index) => (
                <option key={`entry-ticket-select-${index}`} value={ticket.id}>
                  {ticket.name}
                </option>
              ))}
            </select>
          </div>

          {!entryStatusTicketId ? (
            <div className="No-ticket-selected">
              <p>{getTranslation(language, 'selectTicketFromDropdown')}</p>
            </div>
          ) : entryStatusQRCodes.length === 0 ? (
            <div className="No-qr-codes">
              <p>{getTranslation(language, 'noQRCodesGenerated')}</p>
              <p style={{ fontSize: '14px', color: '#999', marginTop: '10px' }}>
                {getTranslation(language, 'generateQRCodesFirst')}
              </p>
              <button 
                onClick={() => setActiveMenu('tickets')}
                className="btn-primary"
                style={{ marginTop: '20px' }}
              >
                {getTranslation(language, 'goToTicketsTab')}
              </button>
            </div>
          ) : (
            <>
              {/* サマリーカード */}
              <div className="EntryStatus-summary">
                <div className="summary-card">
                  <div className="summary-label">{getTranslation(language, 'totalIssued')}</div>
                  <div className="summary-value">{entryStatusQRCodes.length}{getTranslation(language, 'sheets')}</div>
                </div>
                <div className="summary-card entered">
                  <div className="summary-label">{getTranslation(language, 'entered')}</div>
                  <div className="summary-value">
                    {entryStatusQRCodes.filter(qr => qr.entryStatus === 'entered').length}{getTranslation(language, 'sheets')}
                  </div>
                </div>
                <div className="summary-card unused">
                  <div className="summary-label">{getTranslation(language, 'notEntered')}</div>
                  <div className="summary-value">
                    {entryStatusQRCodes.filter(qr => qr.entryStatus !== 'entered').length}{getTranslation(language, 'sheets')}
                  </div>
                </div>
                <div className="summary-card rate">
                  <div className="summary-label">{getTranslation(language, 'entryRate')}</div>
                  <div className="summary-value">
                    {entryStatusQRCodes.length > 0 
                      ? Math.round((entryStatusQRCodes.filter(qr => qr.entryStatus === 'entered').length / entryStatusQRCodes.length) * 100)
                      : 0}%
                  </div>
                </div>
              </div>

              {/* CSV出力ボタン */}
              <div style={{ marginBottom: '20px', textAlign: 'right' }}>
                <button
                  onClick={async () => {
                    try {
                      if (entryStatusQRCodes.length === 0) {
                        showMessage(formatMessage(language, 'cache.noData'), 'error');
                        return;
                      }
                      
                      const csv = generateQRCodeCSV(entryStatusQRCodes);
                      const ticket = tickets.find(t => t.id === entryStatusTicketId);
                      const ticketName = ticket?.name || 'ticket';
                      const fileName = `entry_status_${ticketName}_${new Date().toISOString().split('T')[0]}.csv`;
                      const result = await downloadFile(csv, fileName, 'text/csv;charset=utf-8;');
                      if (result?.savePath) {
                        setLastDownloadedFilePath(result.savePath);
                        setLastDownloadedFileName(result.filename || null);
                      }
                      showMessage(getTranslation(language, 'csvExported'), 'success');
                    } catch (error) {
                      console.error('CSV export failed:', error);
                      showMessage(formatMessage(language, 'csvExportFailed', { error: error.message }), 'error');
                    }
                  }}
                  className="btn-primary"
                  disabled={entryStatusQRCodes.length === 0}
                  title={getTranslation(language, 'csvExportTitle')}
                >
                  📥 {getTranslation(language, 'csvExport')}
                </button>
                {/* Electron: 保存先フォルダで表示 */}
                {lastDownloadedFilePath && window?.electron?.revealInFolder && (
                  <div className="message info" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px' }}>
                    <div style={{ flex: 1, wordBreak: 'break-all' }}>
                      📁 Saved: {lastDownloadedFileName || lastDownloadedFilePath}
                    </div>
                    <button
                      className="btn-secondary"
                      onClick={async () => {
                        try {
                          await window.electron.revealInFolder(lastDownloadedFilePath);
                        } catch (e) {
                          showMessage(`Failed to open folder: ${e?.message || 'unknown error'}`, 'error');
                        }
                      }}
                    >
                      フォルダで表示
                    </button>
                    <button
                      onClick={() => {
                        setLastDownloadedFilePath(null);
                        setLastDownloadedFileName(null);
                      }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}
                      aria-label="dismiss"
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>

              {/* フィルター・ソート・検索 */}
              <div className="EntryStatus-controls">
                <div className="control-group">
                  <label>{getTranslation(language, 'qrCodeSearch')}:</label>
                  <input
                    type="text"
                    value={entryStatusSearchQuery}
                    onChange={(e) => {
                      setEntryStatusSearchQuery(e.target.value);
                      setEntryStatusPage(1);
                    }}
                    placeholder={getTranslation(language, 'searchPlaceholder')}
                    className="search-input"
                    title={getTranslation(language, 'searchTitle')}
                  />
                  {entryStatusSearchQuery && (
                    <button
                      onClick={() => {
                        setEntryStatusSearchQuery('');
                        setEntryStatusPage(1);
                      }}
                      className="clear-search-btn"
                      title={getTranslation(language, 'clearSearch')}
                    >
                      ✕
                    </button>
                  )}
                </div>

                <div className="control-group">
                  <label>{getTranslation(language, 'freeTextSearch')}:</label>
                  <input
                    type="text"
                    value={entryStatusFreeTextSearch}
                    onChange={(e) => {
                      setEntryStatusFreeTextSearch(e.target.value);
                      setEntryStatusPage(1);
                    }}
                    placeholder={getTranslation(language, 'freeTextSearchPlaceholder')}
                    className="search-input"
                  />
                  {entryStatusFreeTextSearch && (
                    <button
                      onClick={() => {
                        setEntryStatusFreeTextSearch('');
                        setEntryStatusPage(1);
                      }}
                      className="clear-search-btn"
                      title={getTranslation(language, 'clearSearch')}
                    >
                      ✕
                    </button>
                  )}
                </div>

                <div className="control-group">
                  <label>{getTranslation(language, 'statusFilter')}:</label>
                  <select
                    value={entryStatusFilter}
                    onChange={(e) => {
                      setEntryStatusFilter(e.target.value);
                      setEntryStatusPage(1);
                    }}
                    className="filter-select"
                  >
                    <option value="all">{getTranslation(language, 'allStatus')}</option>
                    <option value="entered">{getTranslation(language, 'enteredOnly')}</option>
                    <option value="unused">{getTranslation(language, 'unusedOnly')}</option>
                  </select>
                </div>

                <div className="control-group">
                  <label>{getTranslation(language, 'sortBy')}:</label>
                  <select
                    value={entryStatusSort}
                    onChange={(e) => setEntryStatusSort(e.target.value)}
                    className="sort-select"
                  >
                    <option value="time-desc">{getTranslation(language, 'sortTimeDesc')}</option>
                    <option value="time-asc">{getTranslation(language, 'sortTimeAsc')}</option>
                    <option value="qr-asc">{getTranslation(language, 'sortQrAsc')}</option>
                    <option value="qr-desc">{getTranslation(language, 'sortQrDesc')}</option>
                  </select>
                </div>
              </div>

              {/* テーブル */}
              <div className="EntryStatus-list">
                <table className="entry-table">
                  <thead>
                    <tr>
                      <th>{getTranslation(language, 'tableHeaderNo')}</th>
                      <th>{getTranslation(language, 'tableHeaderQRCode')}</th>
                      <th>{getTranslation(language, 'tableHeaderStatus')}</th>
                      <th>{getTranslation(language, 'tableHeaderEntryTime')}</th>
                      <th>{getTranslation(language, 'tableHeaderDevice')}</th>
                      <th>{getTranslation(language, 'tableHeaderFreeText')}</th>
                      <th>{getTranslation(language, 'tableHeaderComment')}</th>
                      <th>{getTranslation(language, 'tableHeaderAction')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      // 検索フィルター適用
                      let filtered = entryStatusQRCodes;
                      
                      if (entryStatusSearchQuery.trim()) {
                        const query = entryStatusSearchQuery.trim();
                        
                        // % を使った検索パターン
                        if (query.startsWith('%') && query.endsWith('%')) {
                          // %ABC% - 部分一致
                          const searchTerm = query.slice(1, -1).toLowerCase();
                          filtered = filtered.filter(qr => 
                            (qr.code || '').toLowerCase().includes(searchTerm)
                          );
                        } else if (query.startsWith('%')) {
                          // %ABC - 後方一致
                          const searchTerm = query.slice(1).toLowerCase();
                          filtered = filtered.filter(qr => 
                            (qr.code || '').toLowerCase().endsWith(searchTerm)
                          );
                        } else if (query.endsWith('%')) {
                          // ABC% - 前方一致
                          const searchTerm = query.slice(0, -1).toLowerCase();
                          filtered = filtered.filter(qr => 
                            (qr.code || '').toLowerCase().startsWith(searchTerm)
                          );
                        } else {
                          // ABC - 部分一致（デフォルト）
                          const searchTerm = query.toLowerCase();
                          filtered = filtered.filter(qr => 
                            (qr.code || '').toLowerCase().includes(searchTerm)
                          );
                        }
                      }
                      
                      // フリー入力検索フィルター適用
                      if (entryStatusFreeTextSearch.trim()) {
                        const searchTerm = entryStatusFreeTextSearch.trim().toLowerCase();
                        filtered = filtered.filter(qr => 
                          (qr.freeText || '').toLowerCase().includes(searchTerm)
                        );
                      }
                      
                      // 状態フィルター適用
                      if (entryStatusFilter === 'entered') {
                        filtered = filtered.filter(qr => qr.entryStatus === 'entered');
                      } else if (entryStatusFilter === 'unused') {
                        filtered = filtered.filter(qr => qr.entryStatus !== 'entered');
                      }

                      // ソート適用
                      const sorted = [...filtered].sort((a, b) => {
                        switch (entryStatusSort) {
                          case 'time-desc':
                            if (a.entryStatus === 'entered' && b.entryStatus !== 'entered') return -1;
                            if (a.entryStatus !== 'entered' && b.entryStatus === 'entered') return 1;
                            if (a.enteredAt && b.enteredAt) {
                              return new Date(b.enteredAt) - new Date(a.enteredAt);
                            }
                            return 0;
                          case 'time-asc':
                            if (a.entryStatus === 'entered' && b.entryStatus !== 'entered') return -1;
                            if (a.entryStatus !== 'entered' && b.entryStatus === 'entered') return 1;
                            if (a.enteredAt && b.enteredAt) {
                              return new Date(a.enteredAt) - new Date(b.enteredAt);
                            }
                            return 0;
                          case 'qr-asc':
                            return (a.code || '').localeCompare(b.code || '');
                          case 'qr-desc':
                            return (b.code || '').localeCompare(a.code || '');
                          default:
                            return 0;
                        }
                      });

                      // ページネーション適用
                      const startIndex = (entryStatusPage - 1) * ITEMS_PER_PAGE;
                      const endIndex = startIndex + ITEMS_PER_PAGE;
                      const paginated = sorted.slice(startIndex, endIndex);
                      const totalPages = Math.ceil(sorted.length / ITEMS_PER_PAGE);

                      return (
                        <>
                          {paginated.map((qr, index) => {
                            // codeが文字列でない場合は文字列に変換
                            let codeString = qr.code;
                            if (typeof codeString !== 'string') {
                              if (codeString && typeof codeString === 'object') {
                                codeString = codeString.value || codeString.code || codeString.text || String(codeString);
                              } else {
                                codeString = String(codeString || '-');
                              }
                            }
                            
                            return (
                              <tr key={`entry-qr-${qr.code}`} className={qr.entryStatus === 'entered' ? 'entered' : 'unused'}>
                                <td>{startIndex + index + 1}</td>
                                <td className="qr-code">{codeString || '-'}</td>
                                <td>
                                  <span className={`status-badge ${qr.entryStatus === 'entered' ? 'entered' : 'unused'}`}>
                                    {qr.entryStatus === 'entered' ? getTranslation(language, 'statusEntered') : getTranslation(language, 'statusUnused')}
                                  </span>
                                </td>
                                <td>
                                  {qr.enteredAt 
                                    ? new Date(qr.enteredAt).toLocaleString('ja-JP', {
                                        year: 'numeric',
                                        month: '2-digit',
                                        day: '2-digit',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })
                                    : '-'}
                                </td>
                                <td className="device-id">
                                  {qr.enteredByDevice ? qr.enteredByDevice.substring(0, 8) + '...' : '-'}
                                </td>
                                <td className="free-text">
                                  {qr.freeText || '-'}
                                </td>
                                <td className="comment-cell">
                                  <input
                                    key={`comment-${qr.code}`}
                                    type="text"
                                    className="comment-input"
                                    defaultValue={qr.comment || ''}
                                    placeholder={getTranslation(language, 'commentPlaceholder')}
                                    onBlur={async (e) => {
                                      const newComment = e.target.value.trim();
                                      const oldComment = qr.comment || '';
                                      if (newComment === oldComment) return;
                                      const result = await saveQRComment(entryStatusTicketId, codeString, newComment);
                                      if (result.success) {
                                        setEntryStatusQRCodes(prev => prev.map(item =>
                                          item.code === qr.code
                                            ? { ...item, comment: newComment || null }
                                            : item
                                        ));
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' && !e.nativeEvent.isComposing) e.target.blur();
                                    }}
                                  />
                                </td>
                                <td className="action-cell">
                                  {qr.entryStatus === 'entered' ? (
                                    <button
                                      className="manual-entry-btn unenter-btn"
                                      onClick={async () => {
                                        if (!window.confirm(getTranslation(language, 'manualUnsetEnteredConfirm'))) return;
                                        const result = await manualSetEntryStatus(entryStatusTicketId, codeString, 'unused');
                                        if (result.success) {
                                          setEntryStatusQRCodes(prev => prev.map(item =>
                                            item.code === qr.code
                                              ? { ...item, entryStatus: null, enteredAt: null, enteredByDevice: null }
                                              : item
                                          ));
                                        } else {
                                          window.alert(result.message || 'エラーが発生しました');
                                        }
                                      }}
                                    >
                                      {getTranslation(language, 'manualUnsetEntered')}
                                    </button>
                                  ) : (
                                    <button
                                      className="manual-entry-btn enter-btn"
                                      onClick={async () => {
                                        if (!window.confirm(getTranslation(language, 'manualSetEnteredConfirm'))) return;
                                        const result = await manualSetEntryStatus(entryStatusTicketId, codeString, 'entered');
                                        if (result.success) {
                                          setEntryStatusQRCodes(prev => prev.map(item =>
                                            item.code === qr.code
                                              ? { ...item, entryStatus: 'entered', enteredAt: new Date().toISOString(), enteredByDevice: 'manual' }
                                              : item
                                          ));
                                        } else {
                                          window.alert(result.message || 'エラーが発生しました');
                                        }
                                      }}
                                    >
                                      {getTranslation(language, 'manualSetEntered')}
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                          {paginated.length === 0 && (
                            <tr>
                              <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                                該当するデータがありません
                              </td>
                            </tr>
                          )}
                        </>
                      );
                    })()}
                  </tbody>
                </table>
              </div>

              {/* ページネーション */}
              {(() => {
                // 検索フィルター適用
                let filtered = entryStatusQRCodes;
                
                if (entryStatusSearchQuery.trim()) {
                  const query = entryStatusSearchQuery.trim();
                  
                  if (query.startsWith('%') && query.endsWith('%')) {
                    const searchTerm = query.slice(1, -1).toLowerCase();
                    filtered = filtered.filter(qr => 
                      (qr.code || '').toLowerCase().includes(searchTerm)
                    );
                  } else if (query.startsWith('%')) {
                    const searchTerm = query.slice(1).toLowerCase();
                    filtered = filtered.filter(qr => 
                      (qr.code || '').toLowerCase().endsWith(searchTerm)
                    );
                  } else if (query.endsWith('%')) {
                    const searchTerm = query.slice(0, -1).toLowerCase();
                    filtered = filtered.filter(qr => 
                      (qr.code || '').toLowerCase().startsWith(searchTerm)
                    );
                  } else {
                    // デフォルトは部分一致
                    const searchTerm = query.toLowerCase();
                    filtered = filtered.filter(qr => 
                      (qr.code || '').toLowerCase().includes(searchTerm)
                    );
                  }
                }
                
                // フリー入力検索フィルター適用
                if (entryStatusFreeTextSearch.trim()) {
                  const searchTerm = entryStatusFreeTextSearch.trim().toLowerCase();
                  filtered = filtered.filter(qr => 
                    (qr.freeText || '').toLowerCase().includes(searchTerm)
                  );
                }
                
                // 状態フィルター適用
                if (entryStatusFilter === 'entered') {
                  filtered = filtered.filter(qr => qr.entryStatus === 'entered');
                } else if (entryStatusFilter === 'unused') {
                  filtered = filtered.filter(qr => qr.entryStatus !== 'entered');
                }
                const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
                
                if (totalPages <= 1) return null;

                return (
                  <div className="EntryStatus-pagination">
                    <button
                      onClick={() => setEntryStatusPage(Math.max(1, entryStatusPage - 1))}
                      disabled={entryStatusPage === 1}
                      className="pagination-btn"
                    >
                      ← 前へ
                    </button>
                    
                    <span className="pagination-info">
                      {entryStatusPage} / {totalPages} ページ
                      （全 {filtered.length} 件中 {Math.min((entryStatusPage - 1) * ITEMS_PER_PAGE + 1, filtered.length)} - {Math.min(entryStatusPage * ITEMS_PER_PAGE, filtered.length)} 件を表示）
                    </span>
                    
                    <button
                      onClick={() => setEntryStatusPage(Math.min(totalPages, entryStatusPage + 1))}
                      disabled={entryStatusPage === totalPages}
                      className="pagination-btn"
                    >
                      次へ →
                    </button>
                  </div>
                );
              })()}
            </>
          )}
        </div>
      )}

      {/* 外部スキャナーモード */}
      {showExternalScanner && (
        <div className="modal-overlay">
          <ExternalScannerPanel onClose={() => setShowExternalScanner(false)} language={language} />
        </div>
      )}

      {/* テンプレート選択ダイアログ */}
      {showTemplateDialog && (
        <div className="modal-overlay" onClick={() => setShowTemplateDialog(false)}>
          <div 
            className="modal-content" 
            onClick={(e) => e.stopPropagation()} 
            style={{ 
              maxWidth: '600px',
              maxHeight: '85vh',
              display: 'flex',
              flexDirection: 'column',
              padding: '0'
            }}
          >
            {/* ヘッダー（固定） */}
            <div style={{ 
              padding: '25px 30px 20px',
              borderBottom: '1px solid #e0e0e0'
            }}>
              <h2 style={{ margin: '0 0 10px 0' }}>🎨 {getTranslation(language, 'selectDesignTemplate')}</h2>
              <p style={{ margin: '0', color: '#666', fontSize: '14px' }}>
                {getTranslation(language, 'enterTicketDesignInfo')}
              </p>
            </div>
            
            {/* スクロール可能なコンテンツ */}
            <div style={{ 
              flex: 1,
              overflowY: 'auto',
              padding: '20px 30px'
            }}>
              {/* デザイン選択（SELECT） */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', fontSize: '14px' }}>
                  {getTranslation(language, 'designLabel')}
                </label>
                <select
                  value={selectedTemplate}
                  onChange={(e) => setSelectedTemplate(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#4CAF50'}
                  onBlur={(e) => e.target.style.borderColor = '#ddd'}
                >
                  {availableTemplates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name} - {template.description}
                    </option>
                  ))}
                </select>
              </div>

              {/* イベント日時入力 */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', fontSize: '14px' }}>
                  {getTranslation(language, 'eventDateLabel')}
                </label>
                <input
                  type="text"
                  value={ticketEventDate}
                  onChange={(e) => setTicketEventDate(e.target.value)}
                  placeholder={getTranslation(language, 'eventDatePlaceholder')}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* フリー入力欄（座席情報など） */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', fontSize: '14px' }}>
                  {getTranslation(language, 'freeInputLabel')}
                </label>
                <div style={{ 
                  background: '#f0f9ff', 
                  border: '1px solid #bae6fd', 
                  borderRadius: '6px', 
                  padding: '12px', 
                  marginBottom: '10px',
                  fontSize: '13px',
                  lineHeight: '1.6'
                }}>
                  <strong>📝 {getTranslation(language, 'freeInputUsage')}</strong>
                  <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                    <li>{getTranslation(language, 'freeInputNote1')}</li>
                    <li><strong>{getTranslation(language, 'freeInputNote2')}</strong> {getTranslation(language, 'freeInputNote2Detail')}</li>
                    <li>{getTranslation(language, 'freeInputNote3')}</li>
                    <li>{getTranslation(language, 'freeInputNote4')}</li>
                  </ul>
                </div>
                <textarea
                  value={ticketSeatInfo}
                  onChange={(e) => setTicketSeatInfo(e.target.value)}
                  placeholder={isHtmlMode ? getTranslation(language, 'freeInputPlaceholderHtml') : getTranslation(language, 'freeInputPlaceholder')}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    fontFamily: isHtmlMode ? 'monospace' : 'inherit',
                    resize: 'vertical'
                  }}
                />
                <div style={{ marginTop: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px' }}>
                    <input
                      type="checkbox"
                      checked={isHtmlMode}
                      onChange={(e) => setIsHtmlMode(e.target.checked)}
                      style={{ marginRight: '6px' }}
                    />
                    {getTranslation(language, 'writeInHtml')}
                  </label>
                </div>
                <div style={{ fontSize: '12px', color: '#999', marginTop: '5px' }}>
                  {isHtmlMode 
                    ? getTranslation(language, 'htmlExample')
                    : getTranslation(language, 'plainTextExample')
                  }
                </div>
              </div>

              {/* レイアウト選択 */}
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '10px' }}>{getTranslation(language, 'printLayoutLabel')}</h3>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <label style={{
                    flex: 1,
                    padding: '12px',
                    border: selectedLayout === 'a4' ? '2px solid #4CAF50' : '2px solid #ddd',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    backgroundColor: selectedLayout === 'a4' ? '#f0f8f0' : 'white',
                    transition: 'all 0.2s'
                  }}>
                    <input
                      type="radio"
                      name="layout"
                      value="a4"
                      checked={selectedLayout === 'a4'}
                      onChange={(e) => {
                        setSelectedLayout(e.target.value);
                        setSelectedTicketSize('standard');
                      }}
                      style={{ marginRight: '8px' }}
                    />
                    <strong>{getTranslation(language, 'a4Layout')}</strong>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      {getTranslation(language, 'a4LayoutDesc')}
                    </div>
                  </label>
                  
                  <label style={{
                    flex: 1,
                    padding: '12px',
                    border: selectedLayout === 'single' ? '2px solid #4CAF50' : '2px solid #ddd',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    backgroundColor: selectedLayout === 'single' ? '#f0f8f0' : 'white',
                    transition: 'all 0.2s'
                  }}>
                    <input
                      type="radio"
                      name="layout"
                      value="single"
                      checked={selectedLayout === 'single'}
                      onChange={(e) => {
                        setSelectedLayout(e.target.value);
                        setSelectedTicketSize('standard');
                      }}
                      style={{ marginRight: '8px' }}
                    />
                    <strong>{getTranslation(language, 'singleLayout')}</strong>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      {getTranslation(language, 'singleLayoutDesc')}
                    </div>
                  </label>
                  
                  <label style={{
                    flex: 1,
                    padding: '12px',
                    border: selectedLayout === 'mobile' ? '2px solid #4CAF50' : '2px solid #ddd',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    backgroundColor: selectedLayout === 'mobile' ? '#f0f8f0' : 'white',
                    transition: 'all 0.2s'
                  }}>
                    <input
                      type="radio"
                      name="layout"
                      value="mobile"
                      checked={selectedLayout === 'mobile'}
                      onChange={(e) => {
                        setSelectedLayout(e.target.value);
                        setSelectedTicketSize('mobile');
                      }}
                      style={{ marginRight: '8px' }}
                    />
                    <strong>{getTranslation(language, 'mobileLayout')}</strong>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      {getTranslation(language, 'mobileLayoutDesc')}
                    </div>
                  </label>
                </div>
              </div>
            </div>

            {/* フッター（固定） */}
            <div style={{ 
              padding: '15px 30px 25px',
              borderTop: '1px solid #e0e0e0',
              display: 'flex',
              gap: '10px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowTemplateDialog(false)}
                className="btn-secondary"
              >
                {getTranslation(language, 'cancel')}
              </button>
              <button
                onClick={() => setShowPreview(true)}
                className="btn-secondary"
              >
                👁️ {getTranslation(language, 'previewButton')}
              </button>
              <button
                onClick={handleGenerateDesignedPDF}
                className="btn-primary"
                disabled={isGeneratingQR}
              >
                {isGeneratingQR ? '⏳ ' + getTranslation(language, 'generating') : '🎨 ' + getTranslation(language, 'generatePdfButton')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* プレビューダイアログ */}
      {showPreview && (
        <div className="modal-overlay" onClick={() => setShowPreview(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ 
            maxWidth: '95%', 
            width: '600px',
            maxHeight: '90vh', 
            overflow: 'auto' 
          }}>
            <h2>👁️ {getTranslation(language, 'previewTitle')}</h2>
            <p style={{ marginBottom: '15px', color: '#666', fontSize: '14px' }}>
              {availableTemplates.find(t => t.id === selectedTemplate)?.name}
            </p>
            
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              padding: '10px',
              backgroundColor: '#f5f5f5',
              borderRadius: '8px',
              marginBottom: '20px',
              overflow: 'auto'
            }}>
              <div 
                style={{
                  maxWidth: '100%',
                  transform: 'scale(0.6)',
                  transformOrigin: 'center center'
                }}
                dangerouslySetInnerHTML={{ 
                  __html: generateTemplatePreview(
                    selectedTemplate, 
                    selectedTicket?.name || getTranslation(language, 'sampleEvent'),
                    generatedQRCodes[0]?.imageData || '',
                    selectedTicketSize,
                    {
                      eventDate: ticketEventDate,
                      seatInfo: ticketSeatInfo,
                      isHtmlMode: isHtmlMode
                    }
                  ) 
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowPreview(false)}
                className="btn-secondary"
              >
                {getTranslation(language, 'closeButton')}
              </button>
              <button
                onClick={() => {
                  setShowPreview(false);
                  handleGenerateDesignedPDF();
                }}
                className="btn-primary"
                disabled={isGeneratingQR}
              >
                {isGeneratingQR ? `⏳ ${getTranslation(language, 'generating')}` : `🎨 ${getTranslation(language, 'generateWithDesign')}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* カスタム確認/入力ダイアログ（Snap/DEBのシステムダイアログ豆腐対策） */}
      {confirmDialog && (
        <div className="Modal-overlay" onClick={handleConfirmCancel}>
          <div className="Modal-dialog" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '420px' }}>
            <div style={{ whiteSpace: 'pre-wrap', marginBottom: '16px', fontSize: '14px', lineHeight: '1.6' }}>
              {confirmDialog.message}
            </div>
            {confirmDialog.isPrompt && (
              <input
                type="text"
                defaultValue={confirmDialog.defaultValue || ''}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleConfirmOk(e.target.value);
                  if (e.key === 'Escape') handleConfirmCancel();
                }}
                ref={(el) => { if (el) el.focus(); }}
                style={{ width: '100%', padding: '8px', fontSize: '14px', borderRadius: '6px', border: '1px solid #ccc', marginBottom: '12px', boxSizing: 'border-box' }}
              />
            )}
            <div className="Modal-buttons">
              <button onClick={handleConfirmCancel} className="btn-secondary">
                {getTranslation(language, 'cancel') || 'Cancel'}
              </button>
              <button
                onClick={(e) => {
                  if (confirmDialog.isPrompt) {
                    const input = e.target.closest('.Modal-dialog').querySelector('input');
                    handleConfirmOk(input?.value ?? confirmDialog.defaultValue ?? '');
                  } else {
                    handleConfirmOk(true);
                  }
                }}
                className="btn-primary"
                autoFocus={!confirmDialog.isPrompt}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketPanel;
